import type { Dict } from "../types";

export const invoicing: Dict = {
  en: {
    "InvoiceRemoteChange.paid": "This invoice was just paid on another device.",
    "InvoiceRemoteChange.cancelled": "This invoice was just cancelled on another device.",
    "InvoiceRemoteChange.modified": "This invoice was just modified on another device.",

    // Invoicing toast messages (errorToast / successToast wrapper)
    "Invoicing.toast.createInvoiceFailed": "Could not create invoice",
    "Invoicing.toast.updateInvoiceFailed": "Could not update invoice",
    "Invoicing.toast.saveInvoiceFailed": "Could not save invoice",
    "Invoicing.toast.deleteInvoiceFailed": "Could not delete invoice",
    "Invoicing.toast.addPaymentFailed": "Could not add payment",
    "Invoicing.toast.voidInvoiceFailed": "Could not void invoice",
    "Invoicing.toast.mergeInvoicesFailed": "Could not merge invoices",
    "Invoicing.toast.unmergeInvoicesFailed": "Could not restore original invoices",
    "Invoicing.toast.printReceiptFailed": "Could not print receipt",
    "Invoicing.toast.receiptNotSentToPrinter": "The receipt was not sent to the printer. You can print it again from the invoice.",
    "Invoicing.toast.receiptPermissionDenied": "Printing the receipt requires the Invoicing permission.",
    "Invoicing.toast.sendReceiptFailed": "Could not send receipt",
    "Invoicing.toast.openCashRegisterFailed": "Could not open cash register",
    "Invoicing.toast.closeCashRegisterFailed": "Could not close cash register",
    "Invoicing.toast.recordMovementFailed": "Could not record cash movement",
    "Invoicing.toast.exportCashReportFailed": "Could not export cash register report",
    "Invoicing.toast.saveDiscountPresetFailed": "Could not save discount preset",
    "Invoicing.toast.deleteDiscountPresetFailed": "Could not delete discount preset",
    "Invoicing.toast.saveTipDistributionFailed": "Could not save tip distribution",
    "Invoicing.toast.finalizeDayFailed": "Could not finalize the day",

    // Invoicing
    "Invoicing.title": "Invoicing",
    "Invoicing.headerSubtitle": "Invoices, cash register and cash report.",
    "Invoicing.tabInvoices": "Invoices",
    "Invoicing.tabCreditNotes": "Credit notes",
    "Invoicing.tileTotalInvoices": "Total invoices",
    "Invoicing.tileLastClosure": "Last closure",
    "CreditNote.empty": "No credit notes yet.",
    "Invoicing.totalRevenue": "Total Revenue",
    "Invoicing.paidInvoices": "Paid Invoices",
    "Invoicing.drafts": "Drafts",
    "Invoicing.cancelledInvoices": "Cancelled Invoices",
    "Invoicing.upgradeRequired": "Invoicing Module Unavailable",
    "Invoicing.upgradeDescription":
      "The invoicing module is not available on your current plan. Please upgrade to the PRO or ENTERPRISE plan to access this feature.",
    "Invoicing.contactUs": "Contact Us to Upgrade",
    "Invoicing.searchPlaceholder": "Search invoices...",
    "Invoicing.filterStatus": "Filter by status",
    "Invoicing.allStatuses": "All Statuses",
    "Invoicing.draft": "Draft",
    "Invoicing.paid": "Paid",
    "Invoicing.partiallyPaid": "Partially Paid",
    "Invoicing.partially_paid": "Partially Paid",
    "Invoicing.cancelled": "Cancelled",
    "Invoicing.voided": "Voided",
    "Invoicing.newInvoice": "New Invoice",
    "Invoicing.invoiceNumber": "Invoice #",
    "Invoicing.customer": "Customer",
    "Invoicing.location": "Location",
    "Invoicing.locationRequired": "Please select a location for each product",
    "Invoicing.noStockAvailable": "No stock available",
    "Invoicing.date": "Date",
    "Invoicing.amount": "Amount",
    "Invoicing.paidAmount": "Paid",
    "Invoicing.balance": "Balance",
    "Invoicing.colInvoiceDiscount": "Invoice discount",
    "Invoicing.colTaxes": "Taxes",
    "Invoicing.colAmountPaid": "Amount paid",
    "Invoicing.employee": "Employee",
    "Invoicing.status": "Status",
    "Invoicing.actions": "Actions",
    "Invoicing.filterByClient": "Filter by Client",
    "Invoicing.filterByEmployee": "Filter by Employee",
    "Invoicing.filterByDate": "Filter by Date",
    "Invoicing.filterByStatus": "Filter by Status",
    "Invoicing.selectAll": "Select All",
    "Invoicing.clear": "Clear",
    "Invoicing.specificDay": "Specific Day",
    "Invoicing.week": "Week",
    "Invoicing.month": "Month",
    "Invoicing.year": "Year",
    "Invoicing.customRange": "Custom Range",
    "Invoicing.selectSunday": "Select a Sunday to display the week (Sunday to Saturday)",
    "Invoicing.selectMonday": "Select a Monday to display the week (Monday to Sunday)",
    "Invoicing.selectRange": "Select start and end dates",
    "Invoicing.loading": "Loading invoices...",
    "Invoicing.noInvoices": "No invoices found",
    "Invoicing.createInvoice": "Create Invoice",
    "Invoicing.editInvoice": "Edit Invoice",
    "Invoicing.createInvoiceDesc": "Create a new invoice for a customer",
    "Invoicing.editInvoiceDesc": "Update invoice details and items",
    "Invoicing.selectCustomer": "Select customer",
    "Invoicing.notes": "Notes",
    "Invoicing.notesPlaceholder": "Add notes for the customer...",
    "Invoicing.summary": "Summary",
    "Invoicing.subtotal": "Subtotal",
    "Invoicing.discountGiftCardBlocked": "Not available on an invoice containing a gift card",
    "Invoicing.discountClearedByGiftCard": "The whole invoice discount was removed: it cannot apply to a gift card.",
    "Invoicing.discountWholeInvoice": "Whole invoice discount",
    "Invoicing.discountThisLine": "Discount on selected lines",
    "Invoicing.discountAppliesToAllLines": "This discount applies to every line. To target a single service, use that line's discount instead.",
    "Invoicing.discountAffectsPay": "Affects employee pay",
    "Invoicing.discountAffectsPayHint": "When on, this discount lowers the commission base of the employees on this invoice.",
    "Invoicing.affectsPayLocked": 'Your permission does not allow you to uncheck "Affects pay". Please ask a manager.',
    "Invoicing.affectsPayLockedHint": "Reserved for authorized staff",
    "Invoicing.affectsPayPresetHint": "The discount reduces the employee's commission",
    "Invoicing.tips": "Tips",
    "Invoicing.tipsSection": "Tips",
    "Invoicing.tipsAmount": "Amount",
    "Invoicing.tipsNegative": "A tip cannot be negative. Enter a positive amount or leave it at 0.",
    "Invoicing.tipsAboveHundred": "A tip cannot exceed 100%. The value was set to 100%.",
    "Invoicing.greencircleFee": "Green Circle fee",
    "Invoicing.greencircleFeeNegative": "The Green Circle fee cannot be negative.",
    "Invoicing.tipsLockedHint": "This invoice is settled, so the tip can no longer be changed here.",

    // Concurrence : ce que `save_invoice` a refuse d'ecraser (SQLSTATE PT409,
    // traduit en 409 Conflict par PostgREST ; c'etait 40001 jusqu'au
    // 2026-09-12, mais PostgREST rejoue les echecs de serialisation).
    // Prefixe NEUF, present dans ce seul module : une meme clef posee dans
    // deux modules i18n est ecrasee EN SILENCE a la fusion.
    //
    // ⚠️ `InvoiceConflict.payments` ne dit PAS « a ete ajoute ». Le detail
    // rendu par la base porte un sens (`direction` : 'added' ou 'removed'),
    // et un encaissement RETIRE (remboursement, annulation) produit le meme
    // refus : un message qui annoncerait un ajout mentirait une fois sur deux.
    "InvoiceConflict.header":
      "This invoice changed while you were editing it ({field}). Reload it to see the up-to-date version.",
    "InvoiceConflict.items":
      "The items on this invoice changed while you were editing it. Reload it to see the up-to-date version.",
    "InvoiceConflict.payments":
      "A payment of {amount} ({method}) changed on this invoice while you were editing it. Reload it to see the up-to-date version.",
    "InvoiceConflict.reload": "Reload the invoice",
    // La facture a disparu sous la modale. ⛔ Ne PAS proposer de la recharger :
    // le rechargement ne trouverait rien.
    "InvoiceConflict.deleted":
      "This invoice was deleted while you were editing it. Nothing was saved.",
    // ⛔ Une des trois lectures de l'ouverture a echoue : il n'y a donc AUCUN
    // instantane de garde, et enregistrer ecraserait le travail de l'autre
    // poste avec des valeurs perimees venues du cache.
    "InvoiceConflict.loadFailed":
      "This invoice could not be fully loaded, so saving is held back to avoid overwriting someone else's work. Reload it to continue.",
    // ⛔ Rien n'a echoue ici : le chargement de la facture est simplement en
    // cours, donc la garde n'est pas encore posee. Ne pas dire « rechargez-la »,
    // le chargement se termine seul et l'enregistrement redevient possible.
    "InvoiceConflict.stillLoading":
      "This invoice is still loading. Nothing was saved: wait a moment, then save again.",
    // Les dix champs d'entête gardés, en libellés lisibles. Un nom de colonne
    // brut (« total_amount ») est illisible au comptoir.
    "InvoiceConflict.field.tips": "the tips",
    "InvoiceConflict.field.discount": "the discount",
    "InvoiceConflict.field.subtotal": "the subtotal",
    "InvoiceConflict.field.total_tax": "the taxes",
    "InvoiceConflict.field.total_amount": "the total",
    "InvoiceConflict.field.notes": "the notes",
    "InvoiceConflict.field.tax_exempt": "the tax exemption",
    "InvoiceConflict.field.settled_externally": "the settled outside Flowcut mark",
    "InvoiceConflict.field.discount_affects_commission": "the effect of the discount on pay",
    "InvoiceConflict.field.issued_at": "the invoice date",
    // Étape carte cadeau non bloquante de `save_invoice` : la facture reste
    // payée (l'argent est bien encaissé), mais l'échec doit se VOIR.
    "Invoicing.errorGiftCardStepFailed":
      "The invoice was saved, but a gift card step failed. Check the card before handing it over.",

    "Invoicing.mergeSelected": "Merge",
    "Invoicing.selectMode": "Select",
    "Invoicing.mergeTitle": "Merge invoices",
    "Invoicing.mergeDescription": "All selected invoices will be replaced by one combined invoice. The originals will be archived but remain linked to their appointments.",
    "Invoicing.mergeWarning": "This action will archive the originals. You can undo via the unmerge button on the combined invoice.",
    "Invoicing.mergeOriginals": "Originals to merge",
    "Invoicing.payer": "Paying customer",
    "Invoicing.mergeTotal": "Combined total",
    "Invoicing.confirmMerge": "Confirm merge",
    "Invoicing.mergeSuccess": "Invoices merged",
    "Invoicing.mergeSuccessDesc": "New invoice {0} created",
    "Invoicing.combinedLabel": "combined",
    "Invoicing.mergedNote": "This invoice combines {0} originals.",
    "Invoicing.recipients": "Recipients",
    "Invoicing.unmerge": "Unmerge",
    "Invoicing.unmergeConfirm": "Restore the original invoices and delete this combined one?",
    "Invoicing.unmergeSuccess": "Invoices restored",
    "InvoiceDialog.tipDistTitle": "Tip distribution per employee",
    "InvoiceDialog.tipDistNegative": "An employee's share cannot be negative. Split the tip into positive amounts.",
    "InvoiceDialog.tipDistAboveHundred": "An employee's share cannot exceed 100%. The value was set to 100%.",
    "InvoiceDialog.tipDistTotal": "Total",
    "InvoiceDialog.tipDistInvalid": "Tip distribution does not match the total tip amount",
    "InvoiceDialog.tipDistError": "Error saving tip distribution",
    "Invoicing.tipsType": "Type",
    "Invoicing.total": "Total",
    "Invoicing.grossTotal": "Gross Total",
    "Invoicing.remaining": "Remaining",
    "Invoicing.taxGST": "GST",
    "Invoicing.taxQST": "QST",
    "Invoicing.invoiceDateLabel": "Invoice date",
    "Invoicing.pickADate": "Pick a date",
    "Invoicing.taxHST": "HST",
    "Invoicing.taxPST": "PST",
    "Invoicing.taxExempt": "Tax Exempt",
    "Invoicing.taxExemptMention": "Tax Exempt",
    "Invoicing.balanceDue": "Balance Due",
    "Invoicing.details": "Details",
    "Invoicing.items": "Items",
    "Invoicing.payments": "Payments",
    "Invoicing.add": "Add",
    "Invoicing.addService": "Service",
    "Invoicing.addProduct": "Product",
    "Invoicing.addOther": "Other",
    "Invoicing.addDiscount": "Discount",
    "Invoicing.addAutonomous": "Self-employed",
    "Invoicing.addForfait": "Package",
    "Invoicing.searchForfait": "Search package...",
    "Invoicing.noForfaitFound": "No package found.",
    "Invoicing.forfaitService": "service",
    "Invoicing.forfaitServices": "services",
    "Invoicing.autonomousBadge": "Self-employed",
    "Invoicing.autonomousZeroPriceLocked": "Price locked at $0",
    "Invoicing.giftCardNoDiscount": "No discount is possible on a gift card",
    "Invoicing.autonomousNoReceiptSent": "Self-employed invoice saved, no receipt sent.",
    "Invoicing.autonomousDialogTitle": "Self-employed items",
    "Invoicing.autonomousDialogHint": "Checked items are set to $0.00 and any discount on them is cleared. If the invoice total reaches $0.00, it is marked as paid, with nothing to collect.",
    "Invoicing.autonomousDialogApply": "Apply",
    "Invoicing.autonomousDialogEmpty": "This invoice has no item that can be marked.",
    "Invoicing.catalogItemMissing": "This item no longer exists in the catalog; its price was reset to $0.00, please check it.",
    "Invoicing.autonomousApplyFailed": "An error occurred while applying the selection; please try again.",
    "Invoicing.autonomousTipMakesPayable": "The tip makes this invoice payable: it is no longer settled outside Flowcut and goes back to draft until the tip is collected.",
    "Invoicing.giftCards": "Gift cards",
    "Invoicing.rechargeMonetary": "Recharge (monetary)",
    "Invoicing.newGiftCard": "New gift card",
    "Invoicing.applyDiscount": "Apply Discount",
    "Invoicing.partialDiscountApplyTo": "Apply to",
    "Invoicing.partialDiscountOf": "of {q}",
    "Invoicing.partialDiscountLess": "One less for {item}",
    "Invoicing.partialDiscountMore": "One more for {item}",
    "Invoicing.partialDiscountSplitHint": "On confirm: {n} with the discount, {rest} without, on two lines.",
    "Invoicing.partialDiscountWholeLine": "The whole line gets the discount.",
    "Invoicing.paidActionDialog.title": "Invoice paid",
    "Invoicing.paidActionDialog.description": "Would you like to print the receipt or email it?",
    "Invoicing.paidActionDialog.print": "Print",
    "Invoicing.paidActionDialog.reprint": "Reprint",
    "Invoicing.paidActionDialog.email": "Email",
    "Invoicing.paidActionDialog.close": "Close",
    "Invoicing.discountDialogDesc": "Select items to apply a discount",
    "Invoicing.discountValue": "Discount Value",
    "Invoicing.discountType": "Discount Type",
    "Invoicing.discountPercentage": "Percentage",
    "Invoicing.discountFixed": "Fixed Amount",
    "Invoicing.originalPrice": "Original",
    "Invoicing.priceAfterDiscount": "After Discount",
    "Invoicing.selectItemsForDiscount": "Select items to discount",
    "Invoicing.noItemsSelected": "Please select at least one item",
    "Invoicing.itemDiscount": "Discount",
    "Invoicing.discountPresets": "Discounts",
    "Invoicing.manageDiscounts": "Manage Discounts",
    "Invoicing.employeePurchases": "Employee purchases",
    "Invoicing.employeePurchasesDesc": "Record off-inventory items an employee took. Does not affect sales, reports or inventory.",
    "Invoicing.employeePurchaseEmployee": "Employee",
    "Invoicing.employeePurchaseSelectEmployee": "Select an employee",
    "Invoicing.employeePurchaseDate": "Date",
    "Invoicing.employeePurchaseItem": "Item",
    "Invoicing.employeePurchaseItemPlaceholder": "Name the purchased item",
    "Invoicing.employeePurchaseAmount": "Cost",
    "Invoicing.employeePurchaseNote": "Note (optional)",
    "Invoicing.employeePurchaseSave": "Save",
    "Invoicing.employeePurchaseSaved": "Employee purchase recorded.",
    "Invoicing.employeePurchaseDeleted": "Purchase deleted.",
    "Invoicing.employeePurchaseValidation": "Pick an employee and describe the item.",
    "Invoicing.employeePurchaseFilterAll": "All employees",
    "Invoicing.employeePurchaseTotal": "Recorded total",
    "Invoicing.employeePurchaseEmpty": "No purchase recorded",
    "Invoicing.employeePurchaseDeleteTitle": "Delete this purchase?",
    "Invoicing.employeePurchaseDeleteDesc": "This record will be permanently deleted.",
    "Invoicing.quickDiscount": "Quick Discount",
    "Invoicing.selectPreset": "Select a saved discount...",
    "Invoicing.noPreset": "Manual (no preset)",
    "Invoicing.newPreset": "New Discount",
    "Invoicing.editPreset": "Edit Discount",
    "Invoicing.presetName": "Discount Name",
    "Invoicing.presetNamePlaceholder": "e.g., Holiday Discount",
    "Invoicing.appliesTo": "Applies to",
    "Invoicing.allItems": "All items",
    "Invoicing.servicesOnly": "Services only",
    "Invoicing.productsOnly": "Products only",
    "Invoicing.validFrom": "Valid from",
    "Invoicing.validUntil": "Valid until",
    "Invoicing.permanent": "Permanent",
    "Invoicing.presetActive": "Active",
    "Invoicing.presetExpired": "Expired",
    "Invoicing.presetUpcoming": "Upcoming",
    "Invoicing.deletePresetConfirm": "Are you sure you want to delete this discount?",
    "Invoicing.deletePresetDesc": "This action cannot be undone. The discount will be permanently removed.",
    "Invoicing.presetSaved": "Discount saved",
    "Invoicing.presetDeleted": "Discount deleted",
    "Invoicing.presetAutoSelected": "items auto-selected by preset",
    "Invoicing.manageDiscountsLink": "Manage discounts",
    "Invoicing.type": "Type",
    "Invoicing.description": "Description",
    "Invoicing.quantity": "Qty",
    "Invoicing.unitPrice": "Price",
    "Invoicing.lineTotal": "Total",
    "Invoicing.selectService": "Select service",
    "Invoicing.selectProduct": "Select product",
    "Invoicing.paymentMethod": "Payment Method",
    "Invoicing.paymentAmount": "Amount",
    "Invoicing.paymentDate": "Date",
    "Invoicing.paymentNotes": "Notes",
    "Invoicing.addPayment": "Add Payment",
    "Invoicing.addRefund": "Add Refund",
    "Invoicing.payment.negativeAmount": "An amount cannot be negative. To give money back to the client, use “Add Refund” on the paid invoice.",
    "Invoicing.payment.negativeRefundAmount": "A refund cannot be negative. Enter the amount to give back to the client as a positive number.",
    "Invoicing.onlineTag.payment": "Paid online",
    "Invoicing.onlineTag.refund": "Refunded through Stripe",
    "Invoicing.onlineTag.chargeback": "Stripe dispute",
    "Invoicing.refundChoice.title": "How would you like to refund?",
    "Invoicing.refundChoice.stripe": "Refund the card through Stripe",
    "Invoicing.refundChoice.paidOn": "collected on {date}",
    "Invoicing.refundChoice.ownerOnly": "You do not have permission to refund a card through Stripe.",
    "Invoicing.refundChoice.manual": "Other refund",
    "Invoicing.refundChoice.saveFirst": "Save your payment changes first, then refund the card.",
    "Invoicing.paidInvoiceInfo": "This invoice is paid. You can add refunds and edit notes.",
    // Lot A, tour de correction du 2026-09-29 : le même bandeau, mais pour une
    // facture PARTIELLEMENT PAYÉE, dont la fenêtre n'est pas encore connue.
    // « paid » y serait faux, et les affordances ne sont pas les mêmes.
    "Invoicing.settledInvoiceCheckingWindow": "This invoice has already taken money. Its amounts stay locked until the correction window is known.",
    // Lot A du 2026-09-27, tâche 9 : le bandeau de la fenêtre de correction.
    // Les cinq motifs de fermeture viennent de `CORRECTION_WINDOW_REASONS`, les
    // deux formes ouvertes se distinguent par l'échéance.
    "Invoicing.correctionWindow.openUntil":
      "This invoice is settled and can still be edited until {deadline}. After that, correcting it means issuing a credit note from the Invoices tab.",
    "Invoicing.correctionWindow.open":
      "This invoice is settled and can still be edited until the end of the day. After that, correcting it means issuing a credit note from the Invoices tab.",
    "Invoicing.correctionWindow.day_closed":
      "This invoice is settled and is not from the current day, so it can no longer be corrected here. To correct it, go to the Invoices tab and issue a credit note.",
    "Invoicing.correctionWindow.register_closed":
      "The cash report covering this invoice is closed, so the invoice can no longer be corrected here. To correct it, go to the Invoices tab and issue a credit note.",
    "Invoicing.correctionWindow.payroll_finalized":
      "The payroll covering this sale is finalized, so the invoice can no longer be corrected here. To correct it, go to the Invoices tab and issue a credit note.",
    "Invoicing.correctionWindow.credit_note_issued":
      "A credit note was already issued on this invoice, so it can no longer be corrected here without breaking the link between the two. To credit more, go to the Invoices tab and issue a second credit note.",
    "Invoicing.correctionWindow.not_correctable":
      "This invoice cannot be corrected here at the moment. To correct it, go to the Invoices tab and issue a credit note.",
    "Invoicing.cash": "Cash",
    "Invoicing.card": "Card",
    "Invoicing.interac": "Interac",
    "Invoicing.other": "Other",
    "Invoicing.cancel": "Cancel",
    "Invoicing.cancelInvoiceAction": "Cancel invoice",
    "Invoicing.cancelInvoiceKeep": "Keep invoice",
    "Invoicing.cancelInvoiceConfirmTitle": "Cancel this invoice?",
    "Invoicing.cancelInvoiceConfirmDesc": "The invoice will be marked cancelled and its balance set to $0. This cannot be undone.",
    "Invoicing.regenerateAction": "Generate again",
    "Invoicing.regenerating": "Generating…",
    "Invoicing.regenerateHint": "This invoice is cancelled and is kept as a record. You can generate a new draft from the appointment.",
    "Invoicing.regenerateConfirmTitle": "Generate a new invoice?",
    "Invoicing.regenerateConfirmDesc": "A new draft invoice will be created from the appointment's services, at the cancelled invoice's prices. The cancelled invoice is kept, read-only.",
    // ⚠️ REMIS LE 2026-09-30 (lot B1, tâche 7), après avoir été retiré le
    //    2026-09-27 par l'arbitrage R5. La raison du retrait était juste à
    //    moitié : une facture qui porte de l'argent ne s'annule plus, mais les
    //    factures DÉJÀ annulées qui en portent existent toujours. Mesuré en
    //    production le 2026-09-30 : 52 factures, 3 510,60 $, 6 salons, dont 45
    //    rattachées à un rendez-vous et donc RÉGÉNÉRABLES. Pour elles,
    //    l'avertissement est exact.
    // ⛔ The drawer sentence was REMOVED on 2026-09-30: batch B1 task 3 stopped
    //    counting a cancelled invoice's money in the cash register report, so
    //    the claim became false in the very case this banner covers.
    "Invoicing.regenerateWarnPayments": "This cancelled invoice still carries {amount} in recorded payments. The new invoice starts at $0 paid. Only collect again if the client actually pays again.",
    "Invoicing.regenerateSuccess": "New draft invoice created",
    "Invoicing.toast.regenerateFailed": "Could not generate the invoice",
    "Invoicing.toast.regenerateAlreadyExists": "This appointment already has an active invoice",
    "Invoicing.toast.regenerateAppointmentCancelled": "The appointment was cancelled or missed: no invoice can be generated",
    "Invoicing.toast.regenerateInMerge": "This appointment is part of a merged invoice: unmerge it first",
    "Invoicing.toast.regenerateNoServices": "No active service on this appointment",
    "Invoicing.toast.regeneratePlanRequired": "Available on the Pro and Enterprise plans",
    "Invoicing.toast.regenerateForbidden": "You do not have permission to do this",
    "Invoicing.restoreInventoryLabel": "Return the products to stock",
    "Invoicing.carryToBalance": "Carry to the client balance",
    "Invoicing.carryToBalanceConfirmDesc": "This invoice will be added to the client's balance as an amount owed. Use this only when the client left without paying: a draft invoice on its own is not a debt.",
    "Invoicing.carryToBalanceAction": "Carry to balance",
    "Invoicing.carryToBalancePending": "Carrying…",
    "Invoicing.carryToBalanceSuccess": "Invoice carried to the client's balance.",
    "Invoicing.carryToBalanceFailed": "Could not carry this invoice to the balance.",
    "CreditNote.issueAction": "Issue credit note",
    "CreditNote.dialogTitle": "Issue a credit note",
    "CreditNote.loading": "Loading invoice…",
    "CreditNote.linesToCredit": "Lines to credit",
    "CreditNote.noCreditableLines": "This invoice has no creditable lines.",
    "CreditNote.reason": "Reason",
    "CreditNote.reasonDetails": "Details (optional)",
    "CreditNote.refundMethod": "Refund method",
    "CreditNote.restoreInventory": "Return the products to stock",
    "CreditNote.notes": "Notes (optional)",
    "CreditNote.subtotal": "Subtotal",
    "CreditNote.tax": "Tax",
    "CreditNote.totalLabel": "Credit note total",
    "CreditNote.submit": "Issue credit note",
    "CreditNote.submitting": "Issuing…",
    "CreditNote.listTitle": "Credit notes",
    "CreditNote.reason.full_refund": "Full refund",
    "CreditNote.reason.partial_refund": "Partial refund",
    "CreditNote.reason.pricing_error": "Pricing error",
    "CreditNote.reason.service_issue": "Service issue",
    "CreditNote.reason.duplicate_charge": "Duplicate charge",
    "CreditNote.reason.goodwill": "Goodwill gesture",
    "CreditNote.reason.other": "Other",
    "CreditNote.col.number": "Number",
    "CreditNote.col.invoice": "Invoice",
    "CreditNote.col.customer": "Customer",
    "CreditNote.col.reason": "Reason",
    "CreditNote.col.amount": "Amount",
    "CreditNote.col.date": "Date",
    "CreditNote.col.print": "Print",
    "CreditNote.toast.created": "Credit note issued",
    "CreditNote.toast.createFailed": "Could not issue the credit note",
    "CreditNote.toast.pdfFailed": "Could not generate the credit note PDF",
    "CreditNote.emptyWhat": "A credit note reverses all or part of a paid invoice. It takes back the taxes in proportion, can return the products to stock, and either gives the money back or leaves it as a credit on the customer's file.",
    "CreditNote.emptyHow": "To issue one, go to the Invoices tab and use the credit note button on a paid or partly paid invoice.",
    "CreditNote.destination": "Where the money goes",
    "CreditNote.destination.refund": "Give the money back",
    "CreditNote.destination.store_credit": "Credit the customer's file",
    "CreditNote.destinationHint.refund": "The money leaves today, through the refund method below.",
    "CreditNote.destinationHint.store_credit": "No money leaves. The amount stays on the customer's file and will be spent on a later invoice.",
    "CreditNote.selectLines": "Select at least one line to credit.",
    "CreditNote.totalMustBePositive": "The credit note total must be above zero.",
    "CreditNote.noInvoice": "No invoice is selected.",
    "CreditNote.alreadyCredited": "Already credited",
    "CreditNote.cashRounding": "Cash rounding",
    "CreditNote.refundAmount": "Amount returned",
    "CreditNote.tipRefunded": "Tip refunded",
    "CreditNote.refundTip": "Also refund the tip ({amount})",
    "CreditNote.refundTipHelp": "Unchecked, the employee keeps the tip. Checked, the salon refunds it out of pocket: the employee's pay does not change.",
    "CreditNote.onlinePaymentNotice": "This invoice was paid online. To return the money to the client's card, use “Refund the card through Stripe” in the invoice window. A credit note returns it another way (cash, other method) or keeps it on file.",
    "CreditNote.previewUnavailable": "Preview unavailable. The amount will be computed on save.",
    "CreditNote.refusedUnknown": "The server refuses this credit note. Reload the page, then try again.",
    "Invoicing.save": "Save",
    "Invoicing.create": "Create",
    "Invoicing.update": "Update",
    "Invoicing.errorSelectCustomer": "Please select a customer",
    "Invoicing.errorAddItem": "Please add at least one item",
    "Invoicing.removeDiscount": "Remove discount",
    "Invoicing.priceRange.aboveMax": "{name}: price {got}$ exceeds the maximum allowed ({max}$).",
    "Invoicing.priceRange.belowMin": "{name}: price {got}$ is below the minimum allowed ({min}$).",
    "Invoicing.priceRange.effectiveBelowMin": "{name}: this discount drops the price to {got}$, below the minimum ({min}$).",
    "Invoicing.quantityRange.aboveMax": "{name}: quantity {got} exceeds the maximum allowed ({max}).",
    "Invoicing.quantityRange.belowMin": "{name}: quantity {got} is below the minimum allowed ({min}).",
    "Invoicing.quantityRange.notFixed": "{name}: quantity is locked to {fixed} for this service.",
    "Invoicing.quantityRange.mustBeInteger": "Quantity must be a whole number for this service.",
    "Invoicing.zeroAmountConfirm.title": "Confirm $0 payment",
    "Invoicing.zeroAmountConfirm.message": "The service \"{name}\" has a price of $0. Do you really want to mark this invoice as paid?",
    "Invoicing.zeroAmountConfirm.confirm": "Confirm",
    "Invoicing.zeroAmountConfirm.cancel": "Cancel",
    "Invoicing.complimentaryConfirm.title": "Is this invoice complimentary?",
    "Invoicing.complimentaryConfirm.withPayment": "The total of this invoice is $0 and you entered a $0 payment.",
    "Invoicing.complimentaryConfirm.withoutPayment": "The total of this invoice is $0 and no payment is entered.",
    "Invoicing.complimentaryConfirm.consequence": "If it is complimentary, it will be considered paid and counted in your reports.",
    "Invoicing.complimentaryConfirm.no": "No, keep as draft",
    "Invoicing.complimentaryConfirm.yes": "Yes, it is complimentary",
    "Invoicing.errorMissingEmployeeOrCustomer": "Each service, product, or other item must have an employee and customer assigned",
    "Invoicing.errorCancelledInvoice": "Cannot modify a cancelled invoice",
    "Invoicing.errorRefundedInvoice": "Cannot modify a refunded invoice",
    "Invoicing.errorLockedInvoice": "This invoice is locked and cannot be modified (cancelled or refunded)",
    "Invoicing.cancelledAutoNote": "Invoice cancelled: balance due automatically set to $0",
    "Invoicing.errorInventoryReversalFailed": "Inventory could not be restored after cancellation. Please verify stock levels manually.",
    // ⛔ CE TEXTE A ÉTÉ CORRIGÉ le 2026-09-12 : il disait « The invoice was not
    //    marked as paid », ce qui est FAUX depuis la décision du 2026-09-05.
    //    L'encaissement est enregistré (l'argent est bien entré), seule la
    //    sortie de stock a échoué. La phrase mentait, et elle mentait sous un
    //    message de succès qui part deux lignes plus loin.
    "Invoicing.errorInventoryMovementFailed": "The payment was recorded, but the stock could not be taken out. Check the stock, then save the invoice again to retry the deduction.",
    "Invoicing.errorInsufficientStock": "Insufficient stock for:",
    // Stock négatif permis (spec 2026-10-09, section 5).
    "Invoicing.negativeStockTitle": "This stock is at zero",
    "Invoicing.negativeStockAt": '{product} has no stock left at "{location}".',
    "Invoicing.negativeStockNoLocation": "{product} has no stock left.",
    "Invoicing.negativeStockQuestion": "Do you want to proceed with the sale? The stock will be recorded as negative.",
    "Invoicing.negativeStockNo": "No",
    "Invoicing.negativeStockYes": "Yes, sell",
    "Invoicing.negativeStockSaveTitle": "Insufficient stock",
    "Invoicing.negativeStockSaveIntro": "These products will go below zero:",
    "Invoicing.negativeStockSaveLine": "{product}: {sold} sold, {available} in stock (stock after the sale: {after})",
    "Invoicing.negativeStockSaveLineOne": "{product}: 1 sold, {available} in stock (stock after the sale: {after})",
    "Invoicing.negativeStockSaveLineAt": '{product} at "{location}": {sold} sold, {available} in stock (stock after the sale: {after})',
    "Invoicing.negativeStockSaveLineAtOne": '{product} at "{location}": 1 sold, {available} in stock (stock after the sale: {after})',
    "Invoicing.negativeStockSaveQuestion": "Do you want to save the invoice anyway?",
    "Invoicing.negativeStockSaveBack": "Back to the invoice",
    "Invoicing.negativeStockSaveConfirm": "Save",
    "Invoicing.invoiceCreated": "Invoice created",
    "Invoicing.invoiceUpdated": "Invoice updated",
    "Invoicing.error": "Error",
    "Invoicing.partial": "Partial",
    "Invoicing.refunded": "Refunded",
    "Invoicing.unknown": "Unknown",
    "Invoicing.customerRequired": "Customer *",
    "Invoicing.statusLabel": "Status",
    "Invoicing.tabDetails": "Details",
    "Invoicing.tabItems": "Items",
    "Invoicing.tabPayments": "Payments",
    "Invoicing.descriptionPlaceholder": "Description",
    "Invoicing.method": "Method",
    "Invoicing.creditCard": "Credit Card",
    "Invoicing.online": "Online",
    "PaymentLinkButton.send": "Send a payment link",
    "PaymentLinkButton.copy": "Copy",
    "PaymentLinkButton.copied": "Copied",
    "PaymentLinkButton.errorTitle": "Could not create the link",
    "PaymentLinkButton.errorBody": "Please try again in a moment.",
    "PaymentLinkButton.revoke": "Cancel the link",
    "PaymentLinkButton.revokeHint": "The link stops working immediately. Any payment already made is kept.",
    "PaymentLinkButton.revokeErrorTitle": "Could not cancel the link",
    "PaymentLinkButton.activeLink": "A payment link is active",
    "PaymentLinkButton.sentByEmail": "Sent by email to",
    "PaymentLinkButton.sentBySms": "Sent by text to",
    "PaymentLinkButton.showQr": "No contact details: show this code to the customer",
    "PaymentLinkButton.skipped.no_email": "No email on file for this customer.",
    "PaymentLinkButton.skipped.invalid_email": "The email on file looks invalid.",
    "PaymentLinkButton.skipped.no_phone": "No phone number on file for this customer.",
    "PaymentLinkButton.skipped.invalid_phone": "The phone number on file looks invalid.",
    "PaymentLinkButton.skipped.missing_contact": "The chosen channel has no contact details on file.",
    "PaymentLinkDialog.title": "Send this payment link?",
    "PaymentLinkDialog.loading": "Checking the customer's contact details…",
    "PaymentLinkDialog.channel.email": "By email",
    "PaymentLinkDialog.channel.sms": "By text message",
    "PaymentLinkDialog.channel.qr": "Show a QR code at the counter",
    "PaymentLinkDialog.pickAChannel": "Choose how to send it first.",
    "PaymentLinkDialog.confirm": "Send",
    "PaymentLinkDialog.cancel": "Cancel",
    "PaymentLinkButton.revokeConfirmTitle": "Cancel this payment link?",
    "PaymentLinkButton.revokeConfirmBody": "The customer will no longer be able to pay with the link they received. Any payment already made is kept.",
    "PaymentLinkButton.revokeConfirm": "Cancel the link",
    "PaymentLinkButton.sendFailedBody": "This could not be sent. Check the invoice's status and the customer's contact details, then try again.",
    "PaymentLinkButton.sendFailedTitle": "This could not be sent",
    "PaymentLinkButton.previewErrorTitle": "Could not prepare the send",
    "PaymentLinkButton.previewErrorBody": "Please try again in a moment.",
    "PaymentLinkButton.nothingToCollectTitle": "Nothing left to collect",
    "PaymentLinkButton.nothingToCollectBody": "This invoice has nothing left to collect. Check whether the customer already paid at the counter.",
    "PaymentLinkButton.createdNotDelivered": "The link was created, but it could not be sent. Copy it and give it to the customer yourself.",
    "PaymentLinkButton.forbiddenSendBody": "You do not have permission to send a payment link for this invoice. Ask a manager.",
    "PaymentLinkButton.forbiddenRevokeBody": "You do not have permission to cancel this payment link. Ask a manager.",
    "PaymentLinkButton.accessCheckUnavailableBody": "Your permissions could not be verified. Please try again in a moment.",
    "PaymentLinkButton.invoiceRefundedBody": "This invoice has been refunded. No payment link can be sent.",
    "PaymentLinkSave.title": "A payment link is active",
    "PaymentLinkSave.body": "A customer may be looking at this invoice's payment link right now. To edit the invoice, the link must be cancelled first. A customer who clicks it will see that it is no longer valid.",
    "PaymentLinkSave.confirm": "Cancel the link and save",
    "PaymentLinkSave.confirming": "Cancelling the link...",
    "PaymentLinkSave.keep": "Keep the link",
    "PaymentLinkSave.askManager": "A payment link is active for this invoice. Ask a manager to cancel it before editing the invoice.",
    "PaymentLinkSave.close": "Close",
    "PaymentLinkSave.revokeFailedBody": "Nothing was saved. Please try again in a moment.",
    "PaymentLinkSave.nothingSaved": "Nothing was saved.",
    "PaymentLinkDialog.channelsLabel": "Delivery channel",
    "Invoicing.debitCard": "Debit",
    "Invoicing.visa": "Visa",
    "Invoicing.mastercard": "Mastercard",
    "Invoicing.amex": "AMEX",
    "Invoicing.eTransfer": "E-Transfer",
    "Invoicing.check": "Check",
    "Invoicing.notesLabel": "Notes",
    "Invoicing.saving": "Saving...",
    "Invoicing.deleteTitle": "Delete Invoice",
    "Invoicing.deleteConfirm": "Are you sure you want to delete this invoice? This action cannot be undone.",
    "Invoicing.invoiceDeleted": "Invoice deleted successfully",
    "Invoicing.deleteSelected": "Delete selected",
    "Invoicing.deleteSelectedTitle": "Delete selected invoices",
    "Invoicing.deleteSelectedConfirm": "Are you sure you want to delete the {count} selected invoice(s)? This action cannot be undone.",
    "Invoicing.deleteSelectedDisabledHint": "Only draft invoices can be deleted",
    "Invoicing.invoicesDeleted": "{count} invoice(s) deleted successfully",
    "Invoicing.errorCannotDeleteNonDraft": "Only draft invoices can be deleted",
    "Invoicing.soldViaInvoice": "Sold via invoice",
    "Invoicing.selectSection": "Select a section",
    "Invoicing.selectSubsection": "Select a subsection",
    "Invoicing.noServicesInSubsection": "No services in this subsection",
    "Invoicing.filterByCategory": "Filter by Category",
    "Invoicing.filterBySupplier": "Filter by Supplier",
    "Invoicing.activeFilters": "Active filters",
    "Invoicing.clearAllFilters": "Clear all",
    "Invoicing.noProductsMatchingFilters": "No products matching filters",
    "Invoicing.searchAndSelectService": "Search and select a service...",
    "Invoicing.searchService": "Search services...",
    "Invoicing.noServiceFound": "No service found.",
    "Invoicing.searchAndSelectProduct": "Search and select a product...",
    "Invoicing.searchProduct": "Search products...",
    "Invoicing.noProductFound": "No product found.",
    "Invoicing.barcodeNotFoundTitle": "No product found",
    "Invoicing.barcodeNotFoundDescriptionPrefix": "No product matches barcode ",
    "Invoicing.barcodeOkButton": "OK",
    "Invoicing.filters": "Filters",
    "Invoicing.active": "active",
    "Invoicing.category": "Category",
    "Invoicing.supplier": "Supplier",
    "Invoicing.uncategorized": "Uncategorized",
    "Invoicing.variablePrice": "Variable price",
    "Invoicing.startingFrom": "Starting from",
    "Invoicing.otherServices": "Other Services",
    "Invoicing.typeService": "Service",
    "Invoicing.typeProduct": "Product",
    "Invoicing.typeOther": "Other",
    "Invoicing.show": "Show",
    "Invoicing.perPage": "per page",
    "Invoicing.pageInfo": "Page {current} of {total}",
    "Invoicing.showingResults": "Showing {start} to {end} of {total} invoices",
    "Invoicing.giftCard": "Gift Card",
    "Invoicing.selectGiftCard": "Select Gift Card",
    "Invoicing.linkedGiftCards": "Linked Gift Cards",
    "Invoicing.enterGiftCardCode": "Enter Gift Card Code",
    "Invoicing.giftCardCode": "Gift Card Code",
    "Invoicing.giftCardBalance": "Balance",
    "Invoicing.giftCardLookup": "Look Up",
    "Invoicing.giftCardLookingUp": "Looking up...",
    "Invoicing.giftCardNotFound": "Gift card not found",
    "Invoicing.giftCardInactive": "This gift card is not active",
    "Invoicing.giftCardExpired": "This gift card has expired",
    "Invoicing.giftCardNoBalance": "This gift card has no remaining balance",
    "Invoicing.giftCardWrongSalon": "This gift card belongs to another salon",
    "Invoicing.giftCardNoLinked": "No linked gift cards found for this customer",
    "Invoicing.giftCardOrEnterCode": "Or enter a gift card code manually",
    "Invoicing.giftCardConfirm": "Use This Card",
    "Invoicing.addGiftCardRecharge": "Recharge Gift Card",
    "Invoicing.giftCardRecharge": "Gift Card Recharge",
    "Invoicing.typeGiftCardRecharge": "Recharge",
    "Invoicing.giftCardRechargeNoGCPayment": "Cannot pay with gift card",
    "Invoicing.selectCardToRecharge": "Select a gift card to recharge",
    "Invoicing.noRechargeableCards": "No rechargeable gift cards found",
    "Invoicing.giftCardPurchase": "Gift Card Purchase",
    "Invoicing.addGiftCardPurchase": "New Gift Card",
    "Invoicing.typeGiftCardPurchase": "Purchase",
    "Invoicing.giftCardPurchaseAmount": "Amount",
    "Invoicing.giftCardExpirationDate": "Expiration Date (optional)",
    "Invoicing.giftCardPurchaseNotes": "Notes (optional)",
    "Invoicing.addGiftCardPurchaseItem": "Add Gift Card",
    "Invoicing.addGiftCardItemsPurchase": "New Items Gift Card",
    "Invoicing.giftCardItemsList": "Items on Card",
    "Invoicing.giftCardItemsTotal": "Total",
    "Invoicing.giftCardMatchedItems": "Matched Items",
    "Invoicing.giftCardSelectItemToRedeem": "Select an item to redeem",
    "Invoicing.giftCardItemRedeemed": "Redeemed",
    "Invoicing.giftCardItemAvailable": "Available",
    "Invoicing.giftCardMonetaryCards": "Monetary Gift Cards",
    "Invoicing.giftCardServiceUnavailable": "Service no longer available",
    "Invoicing.giftCardProductUnavailable": "Product no longer available",
    "Invoicing.perServiceEmployee": "Assign employee per service",
    "Invoicing.selectEmployee": "Select employee",
    "Invoicing.employeeNotQualifiedTag": "(does not perform this service)",
    "Invoicing.employeeNotQualifiedWarning": "This employee does not perform this service: price left unchanged, please verify.",
    "Invoicing.perServiceCustomer": "Assign customer per service",
    "Invoicing.selectItemCustomer": "Select customer",
    "Invoicing.searchClient": "Search by name, email, phone...",
    "Invoicing.noClientFound": "No client found.",
    "Invoicing.print": "Print",
    "Invoicing.viewInvoice": "View invoice",
    "Invoicing.removeItem": "Remove item",
    "Invoicing.removePayment": "Remove payment",
    "Invoicing.toast.mergeNeedsInvoicingPermission": "This action requires the Invoicing permission.",
    "Invoicing.printClientCopy": "Client copy",
    "Invoicing.printBusinessCopy": "Merchant copy",
    "Invoicing.printBothCopies": "Both copies",
    "Invoicing.sendByEmail": "Send by email",
    "Invoicing.sendReceiptByEmail": "Send receipt by email",
    "Invoicing.recipientEmail": "Recipient email",
    "Invoicing.selectRecipient": "Choose a recipient",
    "Invoicing.noEmailOnFile": "No email on file",
    "Invoicing.noEmailOnFileForCustomer": "No email on file for {name}. Enter one manually above.",
    "Invoicing.send": "Send",
    "Invoicing.sending": "Sending...",
    "Invoicing.receiptSentSuccess": "Receipt sent successfully!",
    "Invoicing.receiptSentError": "Error sending receipt",

    // Cash Register
    "CashRegister.title": "Cash Register",
    "CashRegister.open": "Open",
    "CashRegister.closed": "Closed",
    "CashRegister.todayInvoices": "Today's Invoices",
    "CashRegister.reportTitle": "Cash Report",
    "CashRegister.noClosingYet": "No closing yet",
    "CashRegister.openRegister": "Open Register",
    "CashRegister.closeRegister": "Close Register",
    "CashRegister.openingAmount": "Opening Amount",
    "CashRegister.prefilledFromClosing": "Pre-filled from the closing of",
    "CashRegister.closingAmount": "Counted Amount",
    "CashRegister.expectedAmount": "Expected Amount",
    "CashRegister.variance": "Variance",
    "CashRegister.cashBalance": "Cash balance",
    "CashRegister.currentBalance": "Current Balance",
    "CashRegister.cashReceived": "Cash Received",
    "CashRegister.manualAdjustments": "Manual Adjustments",
    "CashRegister.addCash": "Add Cash",
    "CashRegister.withdrawCash": "Withdraw Cash",
    "CashRegister.amount": "Amount",
    "CashRegister.description": "Description",
    "CashRegister.movements": "Movements",
    "CashRegister.movementType": "Type",
    "CashRegister.movementTime": "Time",
    "CashRegister.cashIn": "Cash In",
    "CashRegister.cashOut": "Cash Out",
    "CashRegister.manualAdd": "Manual Add",
    "CashRegister.manualWithdraw": "Manual Withdraw",
    "CashRegister.openingMovement": "Opening",
    "CashRegister.closingMovement": "Closing",
    "CashRegister.confirmClose": "Are you sure you want to close the register?",
    "CashRegister.registerOpened": "Cash register opened successfully",
    "CashRegister.registerClosed": "Cash register closed",
    "CashRegister.alreadyOpen": "A cash register is already open for this salon. It is now displayed.",
    "CashRegister.alreadyClosed": "This cash register was already closed. The closing already recorded is kept.",
    "CashRegister.movementAdded": "Movement added successfully",
    "CashRegister.cashBlockedTitle": "Cash Register Closed",
    "CashRegister.cashBlockedDescription": "Cash payments are not available when the register is closed. Please open the register first.",
    "CashRegister.cashBlockedAskManager": "The cash register is closed. Ask a manager to open it to take cash payments.",
    "CashRegister.notes": "Notes",
    "CashRegister.runningBalance": "Running Balance",
    "CashRegister.noMovements": "No movements yet",
    "CashRegister.session": "Session",
    "CashRegister.openedBy": "Opened by",
    "CashRegister.closedBy": "Closed by",
    "CashRegister.revenueByPayment": "Revenue by Payment Type",
    "CashRegister.totalCollected": "Total collected",
    "CashRegister.salesSummary": "Sales Summary",
    "CashRegister.services": "Services",
    "CashRegister.products": "Products",
    "CashRegister.salesBeforeTax": "Sales before tax",
    "CashRegister.greencircleFees": "Green Circle fees",
    "CashRegister.salesWithTax": "Sales with tax",
    "CashRegister.giftCardsIssued": "Gift cards issued",
    "CashRegister.totalSalesAndGC": "Total sales and gift cards",
    "CashRegister.salesByEmployee": "Sales by Employee (before tax)",
    "CashRegister.tipsByEmployee": "Tips by Employee",
    "CashRegister.cashTips": "Cash",
    "CashRegister.electronicTipsLabel": "Electronic",
    "CashRegister.tipWithdrawnNote": "Tip: invoice {number}",
    "CashRegister.tipWithdrawSkippedRegisterClosed": "Register closed: tip not withdrawn.",
    "CashRegister.totalBeforeTax": "Total before tax",
    "CashRegister.grandTotal": "Grand total",
    "CashRegister.downloadReport": "Print Report",
    "CashRegister.printReceipt": "Print Receipt",
    "CashRegister.clientReceipt": "Client Receipt",
    "CashRegister.businessReceipt": "Business Receipt",
    "CashRegister.closingReport": "Closing Report",
    "CashRegister.selectSession": "Select a session",
    "CashRegister.activeSession": "Active session (provisional)",
    "CashRegister.count": "Count",
    "CashRegister.bills": "Bills",
    "CashRegister.coins": "Coins",
    "CashRegister.denominationTotal": "Total",
    "CashRegister.openingDenominations": "Opening Breakdown",
    "CashRegister.closingDenominations": "Closing Breakdown",
    "CashRegister.denominationBreakdown": "Denomination Breakdown",
    "CashRegister.cashRounding": "Rounding",
    "CashRegister.amountReceived": "Amount Received",
    "CashRegister.changeDue": "Change Due",
    "CashRegister.invalidAmount": "Enter an amount greater than zero.",
    "CashRegister.withdrawExceedsBalance": "Withdrawal exceeds the drawer balance",
    "CashRegister.depositDeclaration": "Deposit Declaration",
    "CashRegister.depositDeclarationLabel": "Deposit declaration (vault)",
    "CashRegister.depositDeclarationHint": "Transfer part of the counted cash to the vault. The variance stays on the full drawer; the deposit is carried out of the next opening float.",
    "CashRegister.depositNotePlaceholder": "Note (optional)",
    "CashRegister.depositToVault": "Deposit to vault",
    "CashRegister.carriedFloat": "Carried float",
    "CashRegister.depositExceedsCount": "The deposit exceeds the counted amount.",
    "CashRegister.reconciliation": "Cash Drawer Reconciliation",
    "CashRegister.openingFloat": "Opening float",
    "CashRegister.cashSales": "Cash sales",
    "CashRegister.cashRefunds": "Cash refunds",
    "CashRegister.manualAdditions": "Manual additions",
    "CashRegister.manualWithdrawals": "Manual withdrawals",
    "CashRegister.tipPayouts": "Tips paid from drawer",
    "CashRegister.tipPayout": "Tip paid out",
    "CashRegister.manualMovementsSection": "Manual Movements",
    "CashRegister.accrualNote": "Sales are recognized at issue date (accrual basis); cash reflects payments received during the session (cash basis). The two may legitimately differ.",
    "Invoicing.openDrawer": "Open the drawer",
    "Invoicing.openDrawerFailed": "The drawer could not be opened: printing did not start.",
    "Invoicing.drawerTicketTitle": "MANUAL CASH DRAWER OPENING",
    "Invoicing.drawerTicketDate": "Date",
    "Invoicing.drawerTicketTime": "Time",
    "Invoicing.drawerTicketBy": "By",

    // CashRegister extras
    "CashRegister.taxTPS": "TPS (5%)",
    "CashRegister.taxTVQ": "TVQ (9.975%)",

    // Discount dialog
    "Invoicing.discountPercentageLabel": "Percentage",
    "Invoicing.discountFixedLabel": "Fixed Amount",

    // Gift cards
    "Invoicing.giftCardPlaceholder": "XXXX-XXXX-XXXX",

    // Cash register — tip paid out of the drawer
    "CashRegister.tipWithdrawFailed": "The tip was not recorded in the register. The cash left the drawer with no trace: add a manual movement before closing.",
  },
  fr: {
    "InvoiceRemoteChange.paid": "Cette facture vient d'être payée sur un autre appareil.",
    "InvoiceRemoteChange.cancelled": "Cette facture vient d'être annulée sur un autre appareil.",
    "InvoiceRemoteChange.modified": "Cette facture vient d'être modifiée sur un autre appareil.",

    // Invoicing toast messages (errorToast / successToast wrapper)
    "Invoicing.toast.createInvoiceFailed": "Impossible de créer la facture",
    "Invoicing.toast.updateInvoiceFailed": "Impossible de modifier la facture",
    "Invoicing.toast.saveInvoiceFailed": "Impossible d'enregistrer la facture",
    "Invoicing.toast.deleteInvoiceFailed": "Impossible de supprimer la facture",
    "Invoicing.toast.addPaymentFailed": "Impossible d'ajouter le paiement",
    "Invoicing.toast.voidInvoiceFailed": "Impossible d'annuler la facture",
    "Invoicing.toast.mergeInvoicesFailed": "Impossible de fusionner les factures",
    "Invoicing.toast.unmergeInvoicesFailed": "Impossible de restaurer les factures d'origine",
    "Invoicing.toast.printReceiptFailed": "Impossible d'imprimer le reçu",
    "Invoicing.toast.receiptNotSentToPrinter": "Le reçu n'a pas été envoyé à l'imprimante. Vous pouvez le réimprimer depuis la facture.",
    "Invoicing.toast.receiptPermissionDenied": "L'impression du reçu demande la permission Facturation.",
    "Invoicing.toast.sendReceiptFailed": "Impossible d'envoyer le reçu",
    "Invoicing.toast.openCashRegisterFailed": "Impossible d'ouvrir la caisse",
    "Invoicing.toast.closeCashRegisterFailed": "Impossible de fermer la caisse",
    "Invoicing.toast.recordMovementFailed": "Impossible d'enregistrer le mouvement de caisse",
    "Invoicing.toast.exportCashReportFailed": "Impossible d'exporter le rapport de caisse",
    "Invoicing.toast.saveDiscountPresetFailed": "Impossible d'enregistrer le rabais préétabli",
    "Invoicing.toast.deleteDiscountPresetFailed": "Impossible de supprimer le rabais préétabli",
    "Invoicing.toast.saveTipDistributionFailed": "Impossible d'enregistrer la répartition des pourboires",
    "Invoicing.toast.finalizeDayFailed": "Impossible de finaliser la journée",

    // Invoicing
    "Invoicing.title": "Facturation",
    "Invoicing.headerSubtitle": "Factures, caisse et rapport de caisse.",
    "Invoicing.tabInvoices": "Factures",
    "Invoicing.tabCreditNotes": "Notes de crédit",
    "Invoicing.tileTotalInvoices": "Total factures",
    "Invoicing.tileLastClosure": "Dernière fermeture",
    "CreditNote.empty": "Aucune note de crédit pour le moment.",
    "Invoicing.totalRevenue": "Revenu total",
    "Invoicing.paidInvoices": "Factures payées",
    "Invoicing.drafts": "Brouillons",
    "Invoicing.cancelledInvoices": "Factures annulées",
    "Invoicing.upgradeRequired": "Module de facturation non disponible",
    "Invoicing.upgradeDescription":
      "Le module de facturation n'est pas disponible dans votre plan actuel. Veuillez passer au plan PRO ou ENTREPRISE pour accéder à cette fonctionnalité.",
    "Invoicing.contactUs": "Nous contacter pour mettre à niveau",
    "Invoicing.searchPlaceholder": "Rechercher des factures...",
    "Invoicing.filterStatus": "Filtrer par statut",
    "Invoicing.allStatuses": "Tous les statuts",
    "Invoicing.draft": "Brouillon",
    "Invoicing.paid": "Payée",
    "Invoicing.partiallyPaid": "Partiellement payée",
    "Invoicing.partially_paid": "Partiellement payée",
    "Invoicing.cancelled": "Annulée",
    "Invoicing.voided": "Annulée (comptabilité)",
    "Invoicing.newInvoice": "Nouvelle facture",
    "Invoicing.invoiceNumber": "Facture #",
    "Invoicing.customer": "Client",
    "Invoicing.location": "Emplacement",
    "Invoicing.locationRequired": "Veuillez sélectionner un emplacement pour chaque produit",
    "Invoicing.noStockAvailable": "Aucun stock disponible",
    "Invoicing.date": "Date",
    "Invoicing.amount": "Montant",
    "Invoicing.paidAmount": "Payé",
    "Invoicing.balance": "Solde",
    "Invoicing.colInvoiceDiscount": "Rabais global",
    "Invoicing.colTaxes": "Taxes",
    "Invoicing.colAmountPaid": "Montant payé",
    "Invoicing.employee": "Employé",
    "Invoicing.status": "Statut",
    "Invoicing.actions": "Actions",
    "Invoicing.filterByClient": "Filtrer par client",
    "Invoicing.filterByEmployee": "Filtrer par employé",
    "Invoicing.filterByDate": "Filtrer par date",
    "Invoicing.filterByStatus": "Filtrer par statut",
    "Invoicing.selectAll": "Sélectionner tout",
    "Invoicing.clear": "Effacer",
    "Invoicing.specificDay": "Jour spécifique",
    "Invoicing.week": "Semaine",
    "Invoicing.month": "Mois",
    "Invoicing.year": "Année",
    "Invoicing.customRange": "Intervalle personnalisé",
    "Invoicing.selectSunday": "Sélectionnez un dimanche pour afficher la semaine (dimanche au samedi)",
    "Invoicing.selectMonday": "Sélectionnez un lundi pour afficher la semaine (lundi au dimanche)",
    "Invoicing.selectRange": "Sélectionnez les dates de début et de fin",
    "Invoicing.loading": "Chargement des factures...",
    "Invoicing.noInvoices": "Aucune facture trouvée",
    "Invoicing.createInvoice": "Créer une facture",
    "Invoicing.editInvoice": "Modifier la facture",
    "Invoicing.createInvoiceDesc": "Créer une nouvelle facture pour un client",
    "Invoicing.editInvoiceDesc": "Mettre à jour les détails et articles de la facture",
    "Invoicing.selectCustomer": "Sélectionner un client",
    "Invoicing.notes": "Notes",
    "Invoicing.notesPlaceholder": "Ajouter des notes pour le client...",
    "Invoicing.summary": "Résumé",
    "Invoicing.subtotal": "Sous-total",
    "Invoicing.discountGiftCardBlocked": "Indisponible sur une facture contenant une carte cadeau",
    "Invoicing.discountClearedByGiftCard": "Le rabais sur toute la facture a été retiré : il ne peut pas porter sur une carte cadeau.",
    "Invoicing.discountWholeInvoice": "Rabais sur toute la facture",
    "Invoicing.discountThisLine": "Rabais sur les lignes choisies",
    "Invoicing.discountAppliesToAllLines": "Ce rabais s'applique à toutes les lignes. Pour ne viser qu'un service, utilisez le rabais de cette ligne.",
    "Invoicing.discountAffectsPay": "Affecte la paye employé",
    "Invoicing.discountAffectsPayHint": "Quand l'option est active, ce rabais réduit la base de commission des employés de cette facture.",
    "Invoicing.affectsPayLocked": "Votre permission ne vous permet pas de décocher « Affecte la paye ». Demandez à un responsable.",
    "Invoicing.affectsPayLockedHint": "Réservé aux personnes autorisées",
    "Invoicing.affectsPayPresetHint": "Le rabais réduit la commission de l'employé",
    "Invoicing.tips": "Pourboires",
    "Invoicing.tipsSection": "Pourboires",
    "Invoicing.tipsAmount": "Montant",
    "Invoicing.tipsNegative": "Un pourboire ne peut pas être négatif. Saisissez un montant positif ou laissez 0.",
    "Invoicing.tipsAboveHundred": "Un pourboire ne peut pas dépasser 100 %. La valeur a été ramenée à 100 %.",
    "Invoicing.greencircleFee": "Frais Green Circle",
    "Invoicing.greencircleFeeNegative": "Les frais Green Circle ne peuvent pas être négatifs.",
    "Invoicing.tipsType": "Type",
    // ⚠️ Vouvoiement. Aucune solution de rechange n'est promise ici : on dit
    //    pourquoi c'est ferme, on n'invente pas un contournement.
    "Invoicing.tipsLockedHint": "La facture est réglée : le pourboire ne peut plus être modifié ici.",

    // Concurrence : ce que `save_invoice` a refuse d'ecraser (SQLSTATE PT409,
    // traduit en 409 Conflict par PostgREST ; c'etait 40001 jusqu'au
    // 2026-09-12, mais PostgREST rejoue les echecs de serialisation).
    // ⚠️ Vouvoiement, y compris a l'imperatif : « Rechargez-la ».
    "InvoiceConflict.header":
      "Cette facture a changé pendant que vous la modifiiez ({field}). Rechargez-la pour voir la version à jour.",
    "InvoiceConflict.items":
      "Les articles de cette facture ont changé pendant que vous la modifiiez. Rechargez-la pour voir la version à jour.",
    "InvoiceConflict.payments":
      "Un encaissement de {amount} ({method}) a changé sur cette facture pendant que vous la modifiiez. Rechargez-la pour voir la version à jour.",
    "InvoiceConflict.reload": "Recharger la facture",
    // La facture a disparu sous la modale. ⛔ Ne PAS proposer de la recharger :
    // le rechargement ne trouverait rien.
    "InvoiceConflict.deleted":
      "Cette facture a été supprimée pendant que vous la modifiiez. Rien n'a été enregistré.",
    // ⛔ Une des trois lectures de l'ouverture a echoue : il n'y a donc AUCUN
    // instantane de garde, et enregistrer ecraserait le travail de l'autre
    // poste avec des valeurs perimees venues du cache.
    // ⚠️ Vouvoiement, y compris a l'imperatif : « Rechargez-la ».
    "InvoiceConflict.loadFailed":
      "Cette facture n'a pas pu être chargée entièrement. L'enregistrement est retenu pour ne pas écraser le travail de quelqu'un d'autre. Rechargez-la pour continuer.",
    // ⛔ Rien n'a echoue ici : le chargement de la facture est simplement en
    // cours, donc la garde n'est pas encore posee. Ne pas dire « rechargez-la »,
    // le chargement se termine seul et l'enregistrement redevient possible.
    // ⚠️ Vouvoiement, y compris a l'imperatif : « patientez », « enregistrez ».
    "InvoiceConflict.stillLoading":
      "Cette facture est encore en cours de chargement. Rien n'a été enregistré : patientez un instant, puis enregistrez de nouveau.",
    // Les dix champs d'entête gardés, en libellés lisibles. Un nom de colonne
    // brut (« total_amount ») est illisible au comptoir.
    "InvoiceConflict.field.tips": "le pourboire",
    "InvoiceConflict.field.discount": "le rabais",
    "InvoiceConflict.field.subtotal": "le sous-total",
    "InvoiceConflict.field.total_tax": "les taxes",
    "InvoiceConflict.field.total_amount": "le total",
    "InvoiceConflict.field.notes": "les notes",
    "InvoiceConflict.field.tax_exempt": "l'exemption de taxes",
    "InvoiceConflict.field.settled_externally": "la marque « réglée hors Flowcut »",
    "InvoiceConflict.field.discount_affects_commission": "l'effet du rabais sur la paie",
    "InvoiceConflict.field.issued_at": "la date de la facture",
    // Étape carte cadeau non bloquante de `save_invoice` : la facture reste
    // payée (l'argent est bien encaissé), mais l'échec doit se VOIR.
    "Invoicing.errorGiftCardStepFailed":
      "La facture est enregistrée, mais une étape de carte cadeau a échoué. Vérifiez la carte avant de la remettre.",

    "Invoicing.mergeSelected": "Combiner",
    "Invoicing.selectMode": "Sélectionner",
    "Invoicing.mergeTitle": "Combiner des factures",
    "Invoicing.mergeDescription": "Les factures sélectionnées seront remplacées par une seule facture combinée. Les originales seront archivées mais resteront liées à leurs rendez-vous.",
    "Invoicing.mergeWarning": "Cette action archive les originales. Vous pouvez annuler avec le bouton « Défusionner » sur la facture combinée.",
    "Invoicing.mergeOriginals": "Factures à combiner",
    "Invoicing.payer": "Client payeur",
    "Invoicing.mergeTotal": "Total combiné",
    "Invoicing.confirmMerge": "Confirmer la fusion",
    "Invoicing.mergeSuccess": "Factures combinées",
    "Invoicing.mergeSuccessDesc": "Nouvelle facture {0} créée",
    "Invoicing.combinedLabel": "combinée",
    "Invoicing.mergedNote": "Cette facture combine {0} originales.",
    "Invoicing.recipients": "Destinataires",
    "Invoicing.unmerge": "Défusionner",
    "Invoicing.unmergeConfirm": "Restaurer les factures originales et supprimer cette combinée?",
    "Invoicing.unmergeSuccess": "Factures restaurées",
    "InvoiceDialog.tipDistTitle": "Répartition du pourboire par employé",
    "InvoiceDialog.tipDistNegative": "La part d'un employé ne peut pas être négative. Répartissez le pourboire en montants positifs.",
    "InvoiceDialog.tipDistAboveHundred": "La part d'un employé ne peut pas dépasser 100 %. La valeur a été ramenée à 100 %.",
    "InvoiceDialog.tipDistTotal": "Total",
    "InvoiceDialog.tipDistInvalid": "La répartition du pourboire ne correspond pas au montant total",
    "InvoiceDialog.tipDistError": "Erreur lors de la sauvegarde de la répartition",
    "Invoicing.total": "Total",
    "Invoicing.grossTotal": "Total brut",
    "Invoicing.remaining": "Reste",
    "Invoicing.taxGST": "TPS",
    "Invoicing.taxQST": "TVQ",
    "Invoicing.invoiceDateLabel": "Date de la facture",
    "Invoicing.pickADate": "Choisir une date",
    "Invoicing.taxHST": "TVH",
    "Invoicing.taxPST": "TVP",
    "Invoicing.taxExempt": "Exempt de taxes",
    "Invoicing.taxExemptMention": "Exempt de taxes",
    "Invoicing.balanceDue": "Solde dû",
    "Invoicing.details": "Détails",
    "Invoicing.items": "Articles",
    "Invoicing.payments": "Paiements",
    "Invoicing.add": "Ajouter",
    "Invoicing.addService": "Service",
    "Invoicing.addProduct": "Produit",
    "Invoicing.addOther": "Autre",
    "Invoicing.addDiscount": "Rabais",
    "Invoicing.addAutonomous": "Travailleur autonome",
    "Invoicing.addForfait": "Forfait",
    "Invoicing.searchForfait": "Chercher un forfait...",
    "Invoicing.noForfaitFound": "Aucun forfait trouvé.",
    "Invoicing.forfaitService": "service",
    "Invoicing.forfaitServices": "services",
    "Invoicing.autonomousBadge": "Autonome",
    "Invoicing.autonomousZeroPriceLocked": "Prix verrouillé à 0 $",
    "Invoicing.giftCardNoDiscount": "Aucun rabais n'est possible sur une carte cadeau",
    "Invoicing.autonomousNoReceiptSent": "Facture autonome enregistrée, aucun reçu envoyé.",
    "Invoicing.autonomousDialogTitle": "Articles en travailleur autonome",
    "Invoicing.autonomousDialogHint": "Les articles cochés passent à 0,00 $ et leur rabais est effacé. Si le total de la facture atteint 0,00 $, elle est marquée payée, sans rien à percevoir.",
    "Invoicing.autonomousDialogApply": "Appliquer",
    "Invoicing.autonomousDialogEmpty": "Cette facture ne contient aucun article pouvant être marqué.",
    "Invoicing.catalogItemMissing": "Cet article n'existe plus au catalogue ; son prix a été remis à 0,00 $, veuillez le vérifier.",
    "Invoicing.autonomousApplyFailed": "Une erreur est survenue lors de l'application de la sélection ; veuillez réessayer.",
    "Invoicing.autonomousTipMakesPayable": "Le pourboire rend cette facture payable : elle n'est plus réglée hors Flowcut et repasse en brouillon jusqu'à son encaissement.",
    "Invoicing.giftCards": "Cartes cadeaux",
    "Invoicing.rechargeMonetary": "Recharger (monétaire)",
    "Invoicing.newGiftCard": "Nouvelle carte cadeau",
    "Invoicing.applyDiscount": "Appliquer le rabais",
    "Invoicing.partialDiscountApplyTo": "Appliquer à",
    "Invoicing.partialDiscountOf": "sur {q}",
    "Invoicing.partialDiscountLess": "Un de moins pour {item}",
    "Invoicing.partialDiscountMore": "Un de plus pour {item}",
    "Invoicing.partialDiscountSplitHint": "À la confirmation : {n} avec le rabais, {rest} sans rabais, sur deux lignes.",
    "Invoicing.partialDiscountWholeLine": "Toute la ligne reçoit le rabais.",
    "Invoicing.paidActionDialog.title": "Facture payée",
    "Invoicing.paidActionDialog.description": "Voulez-vous imprimer le reçu ou l'envoyer par courriel ?",
    "Invoicing.paidActionDialog.print": "Imprimer",
    "Invoicing.paidActionDialog.reprint": "Réimprimer",
    "Invoicing.paidActionDialog.email": "Envoyer par courriel",
    "Invoicing.paidActionDialog.close": "Fermer",
    "Invoicing.discountDialogDesc": "Sélectionnez les articles pour appliquer un rabais",
    "Invoicing.discountValue": "Valeur du rabais",
    "Invoicing.discountType": "Type de rabais",
    "Invoicing.discountPercentage": "Pourcentage",
    "Invoicing.discountFixed": "Montant fixe",
    "Invoicing.originalPrice": "Original",
    "Invoicing.priceAfterDiscount": "Après rabais",
    "Invoicing.selectItemsForDiscount": "Sélectionnez les articles auxquels appliquer le rabais",
    "Invoicing.noItemsSelected": "Veuillez sélectionner au moins un article",
    "Invoicing.itemDiscount": "Rabais",
    "Invoicing.discountPresets": "Rabais",
    "Invoicing.manageDiscounts": "Gérer les rabais",
    "Invoicing.employeePurchases": "Achats employés",
    "Invoicing.employeePurchasesDesc": "Enregistre les articles hors inventaire pris par un employé. N'affecte ni les ventes, ni les rapports, ni l'inventaire.",
    "Invoicing.employeePurchaseEmployee": "Employé",
    "Invoicing.employeePurchaseSelectEmployee": "Choisir un employé",
    "Invoicing.employeePurchaseDate": "Date",
    "Invoicing.employeePurchaseItem": "Article",
    "Invoicing.employeePurchaseItemPlaceholder": "Nommez l'article acheté",
    "Invoicing.employeePurchaseAmount": "Coût",
    "Invoicing.employeePurchaseNote": "Note (optionnel)",
    "Invoicing.employeePurchaseSave": "Enregistrer",
    "Invoicing.employeePurchaseSaved": "Achat employé enregistré.",
    "Invoicing.employeePurchaseDeleted": "Achat supprimé.",
    "Invoicing.employeePurchaseValidation": "Choisissez un employé et décrivez l'article.",
    "Invoicing.employeePurchaseFilterAll": "Tous les employés",
    "Invoicing.employeePurchaseTotal": "Total enregistré",
    "Invoicing.employeePurchaseEmpty": "Aucun achat enregistré",
    "Invoicing.employeePurchaseDeleteTitle": "Supprimer cet achat ?",
    "Invoicing.employeePurchaseDeleteDesc": "Cet enregistrement sera supprimé définitivement.",
    "Invoicing.quickDiscount": "Rabais rapide",
    "Invoicing.selectPreset": "Sélectionner un rabais enregistré...",
    "Invoicing.noPreset": "Manuel (aucun preset)",
    "Invoicing.newPreset": "Nouveau rabais",
    "Invoicing.editPreset": "Modifier le rabais",
    "Invoicing.presetName": "Nom du rabais",
    "Invoicing.presetNamePlaceholder": "ex: Rabais des fêtes",
    "Invoicing.appliesTo": "S'applique à",
    "Invoicing.allItems": "Tous les articles",
    "Invoicing.servicesOnly": "Services seulement",
    "Invoicing.productsOnly": "Produits seulement",
    "Invoicing.validFrom": "Valide du",
    "Invoicing.validUntil": "Valide jusqu'au",
    "Invoicing.permanent": "Permanent",
    "Invoicing.presetActive": "Actif",
    "Invoicing.presetExpired": "Expiré",
    "Invoicing.presetUpcoming": "À venir",
    "Invoicing.deletePresetConfirm": "Voulez-vous vraiment supprimer ce rabais ?",
    "Invoicing.deletePresetDesc": "Cette action est irréversible. Le rabais sera supprimé définitivement.",
    "Invoicing.presetSaved": "Rabais enregistré",
    "Invoicing.presetDeleted": "Rabais supprimé",
    "Invoicing.presetAutoSelected": "articles auto-sélectionnés par le preset",
    "Invoicing.manageDiscountsLink": "Gérer les rabais",
    "Invoicing.type": "Type",
    "Invoicing.description": "Description",
    "Invoicing.quantity": "Qté",
    "Invoicing.unitPrice": "Prix",
    "Invoicing.lineTotal": "Total",
    "Invoicing.selectService": "Sélectionner un service",
    "Invoicing.selectProduct": "Sélectionner un produit",
    "Invoicing.paymentMethod": "Méthode de paiement",
    "Invoicing.paymentAmount": "Montant",
    "Invoicing.paymentDate": "Date",
    "Invoicing.paymentNotes": "Notes",
    "Invoicing.addPayment": "Ajouter un paiement",
    "Invoicing.addRefund": "Ajouter un remboursement",
    "Invoicing.payment.negativeAmount": "Un montant ne peut pas être négatif. Pour rendre de l'argent au client, utilisez « Ajouter un remboursement » sur la facture payée.",
    "Invoicing.payment.negativeRefundAmount": "Un remboursement ne peut pas être négatif. Saisissez le montant à rendre au client en positif.",
    "Invoicing.onlineTag.payment": "Payé en ligne",
    "Invoicing.onlineTag.refund": "Remboursé par Stripe",
    "Invoicing.onlineTag.chargeback": "Contestation Stripe",
    "Invoicing.refundChoice.title": "Comment rembourser ?",
    "Invoicing.refundChoice.stripe": "Rembourser la carte par Stripe",
    "Invoicing.refundChoice.paidOn": "encaissé le {date}",
    "Invoicing.refundChoice.ownerOnly": "Vous n'avez pas la permission de rembourser une carte par Stripe.",
    "Invoicing.refundChoice.manual": "Autre remboursement",
    "Invoicing.refundChoice.saveFirst": "Enregistrez d'abord vos modifications de paiement, puis remboursez la carte.",
    "Invoicing.paidInvoiceInfo": "Cette facture est payée. Vous pouvez ajouter des remboursements et modifier les notes.",
    // Lot A, tour de correction du 2026-09-29. Voir la version anglaise.
    "Invoicing.settledInvoiceCheckingWindow": "Cette facture a déjà encaissé de l'argent. Ses montants restent verrouillés le temps de vérifier jusqu'à quand elle reste corrigeable.",
    // Lot A du 2026-09-27, tâche 9 : le bandeau de la fenêtre de correction.
    //
    // ⛔ « un avoir », JAMAIS « note de crédit » (arbitrage R17) : le bouton de
    //    l'interface française dit « Émettre un avoir » (`CreditNote.issueAction`)
    //    et il vit dans l'onglet Factures, pas dans la facture ouverte. Chaque
    //    refus nomme donc l'écran, sinon il envoie chercher un bouton
    //    introuvable.
    // ⛔ `day_closed` n'affirme RIEN sur la fin de la journée (arbitrage R28) :
    //    une facture datée dans le FUTUR reçoit ce motif, la branche serveur
    //    comparant deux dates par `<>`.
    "Invoicing.correctionWindow.openUntil":
      "Cette facture est encaissée et reste modifiable jusqu'au {deadline}. Ensuite, il faudra émettre un avoir depuis l'onglet Factures pour la corriger.",
    "Invoicing.correctionWindow.open":
      "Cette facture est encaissée et reste modifiable jusqu'à la fin de la journée. Ensuite, il faudra émettre un avoir depuis l'onglet Factures pour la corriger.",
    "Invoicing.correctionWindow.day_closed":
      "Cette facture est encaissée et n'est pas du jour courant : elle ne peut plus être corrigée ici. Pour la corriger, allez dans l'onglet Factures et émettez un avoir.",
    "Invoicing.correctionWindow.register_closed":
      "Le rapport de caisse qui couvre cette facture est fermé : elle ne peut plus être corrigée ici. Pour la corriger, allez dans l'onglet Factures et émettez un avoir.",
    "Invoicing.correctionWindow.payroll_finalized":
      "La paie qui couvre cette vente est finalisée : la facture ne peut plus être corrigée ici. Pour la corriger, allez dans l'onglet Factures et émettez un avoir.",
    "Invoicing.correctionWindow.credit_note_issued":
      "Un avoir a déjà été émis sur cette facture : elle ne peut plus être corrigée ici sans rompre le lien entre les deux. Pour créditer davantage, allez dans l'onglet Factures et émettez un second avoir.",
    "Invoicing.correctionWindow.not_correctable":
      "Cette facture ne peut pas être corrigée ici pour le moment. Pour la corriger, allez dans l'onglet Factures et émettez un avoir.",
    "Invoicing.cash": "Comptant",
    "Invoicing.card": "Carte",
    "Invoicing.interac": "Interac",
    "Invoicing.other": "Autre",
    "Invoicing.cancel": "Annuler",
    "Invoicing.cancelInvoiceAction": "Annuler la facture",
    "Invoicing.cancelInvoiceKeep": "Conserver la facture",
    "Invoicing.cancelInvoiceConfirmTitle": "Annuler cette facture ?",
    "Invoicing.cancelInvoiceConfirmDesc": "La facture sera marquée annulée et son solde mis à 0 $. Cette action est irréversible.",
    "Invoicing.regenerateAction": "Générer à nouveau",
    "Invoicing.regenerating": "Génération…",
    "Invoicing.regenerateHint": "Cette facture est annulée et reste conservée comme trace. Vous pouvez générer un nouveau brouillon à partir du rendez-vous.",
    "Invoicing.regenerateConfirmTitle": "Générer une nouvelle facture ?",
    "Invoicing.regenerateConfirmDesc": "Une nouvelle facture brouillon sera créée à partir des services du rendez-vous, aux prix de la facture annulée. La facture annulée est conservée, en lecture seule.",
    // ⚠️ REMIS LE 2026-09-30 (lot B1, tâche 7). Voir la note anglaise
    //    ci-dessus : 52 factures annulées portent encore 3 510,60 $ en
    //    production, dont 45 régénérables.
    // ⛔ L'affirmation sur le tiroir a été RETIRÉE le 2026-09-30 : la tâche 3
    //    du même lot a cessé de compter l'argent d'une facture annulée dans le
    //    rapport de caisse, ce qui rendait cette phrase fausse précisément dans
    //    le cas que ce bandeau couvre.
    "Invoicing.regenerateWarnPayments": "Cette facture annulée porte encore {amount} de paiements enregistrés. La nouvelle facture repart à 0 $ payé : n'encaissez à nouveau que si le client repaye réellement.",
    "Invoicing.regenerateSuccess": "Nouvelle facture brouillon créée",
    "Invoicing.toast.regenerateFailed": "Impossible de générer la facture",
    "Invoicing.toast.regenerateAlreadyExists": "Ce rendez-vous a déjà une facture active",
    "Invoicing.toast.regenerateAppointmentCancelled": "Le rendez-vous a été annulé ou manqué : aucune facture ne peut être générée",
    "Invoicing.toast.regenerateInMerge": "Ce rendez-vous fait partie d'une facture fusionnée : défusionnez-la d'abord",
    "Invoicing.toast.regenerateNoServices": "Aucun service actif sur ce rendez-vous",
    "Invoicing.toast.regeneratePlanRequired": "Disponible sur les forfaits Pro et Enterprise",
    "Invoicing.toast.regenerateForbidden": "Vous n'avez pas la permission de faire ceci",
    "Invoicing.restoreInventoryLabel": "Remettre les produits en stock",
    "Invoicing.carryToBalance": "Porter au solde du client",
    "Invoicing.carryToBalanceConfirmDesc": "Cette facture sera ajoutée au solde du client, comme montant dû. Utilisez ce geste seulement quand le client est parti sans payer : un brouillon à lui seul n'est pas une dette.",
    "Invoicing.carryToBalanceAction": "Porter au solde",
    "Invoicing.carryToBalancePending": "Ajout en cours…",
    "Invoicing.carryToBalanceSuccess": "Facture portée au solde du client.",
    "Invoicing.carryToBalanceFailed": "Impossible de porter cette facture au solde.",
    "CreditNote.issueAction": "Émettre un avoir",
    "CreditNote.dialogTitle": "Émettre un avoir",
    "CreditNote.loading": "Chargement de la facture…",
    "CreditNote.linesToCredit": "Lignes à créditer",
    "CreditNote.noCreditableLines": "Cette facture n'a aucune ligne créditable.",
    "CreditNote.reason": "Raison",
    "CreditNote.reasonDetails": "Détails (optionnel)",
    "CreditNote.refundMethod": "Mode de remboursement",
    "CreditNote.restoreInventory": "Remettre les produits en stock",
    "CreditNote.notes": "Notes (optionnel)",
    "CreditNote.subtotal": "Sous-total",
    "CreditNote.tax": "Taxes",
    "CreditNote.totalLabel": "Total de l'avoir",
    "CreditNote.submit": "Émettre l'avoir",
    "CreditNote.submitting": "Émission…",
    "CreditNote.listTitle": "Notes de crédit",
    "CreditNote.reason.full_refund": "Remboursement complet",
    "CreditNote.reason.partial_refund": "Remboursement partiel",
    "CreditNote.reason.pricing_error": "Erreur de prix",
    "CreditNote.reason.service_issue": "Problème de service",
    "CreditNote.reason.duplicate_charge": "Double facturation",
    "CreditNote.reason.goodwill": "Geste commercial",
    "CreditNote.reason.other": "Autre",
    "CreditNote.col.number": "Numéro",
    "CreditNote.col.invoice": "Facture",
    "CreditNote.col.customer": "Client",
    "CreditNote.col.reason": "Raison",
    "CreditNote.col.amount": "Montant",
    "CreditNote.col.date": "Date",
    "CreditNote.col.print": "Imprimer",
    "CreditNote.toast.created": "Avoir émis",
    "CreditNote.toast.createFailed": "Impossible d'émettre l'avoir",
    "CreditNote.toast.pdfFailed": "Impossible de générer le PDF de l'avoir",
    "CreditNote.emptyWhat": "Un avoir renverse tout ou partie d'une facture payée. Il reprend les taxes au prorata, peut remettre les produits en stock, et soit rend l'argent, soit le laisse en crédit au dossier de la cliente.",
    "CreditNote.emptyHow": "Pour en émettre un, allez dans l'onglet Factures et utilisez le bouton d'avoir sur une facture payée ou partiellement payée.",
    "CreditNote.destination": "Destination de l'argent",
    "CreditNote.destination.refund": "Rendre l'argent",
    "CreditNote.destination.store_credit": "Créditer le dossier du client",
    "CreditNote.destinationHint.refund": "L'argent sort aujourd'hui, par le mode de remboursement ci-dessous.",
    "CreditNote.destinationHint.store_credit": "Aucun argent ne sort. Le montant reste au dossier de la cliente et se dépensera sur une facture ultérieure.",
    "CreditNote.selectLines": "Sélectionnez au moins une ligne à créditer.",
    "CreditNote.totalMustBePositive": "Le total de l'avoir doit être supérieur à zéro.",
    "CreditNote.noInvoice": "Aucune facture n'est sélectionnée.",
    "CreditNote.alreadyCredited": "Déjà créditée",
    "CreditNote.cashRounding": "Arrondi comptant",
    "CreditNote.refundAmount": "Montant rendu",
    "CreditNote.tipRefunded": "Pourboire remboursé",
    "CreditNote.refundTip": "Rembourser aussi le pourboire ({amount})",
    "CreditNote.refundTipHelp": "Décochée, l'employé garde son pourboire. Cochée, le salon le rend au client de sa poche : la paie de l'employé ne change pas.",
    "CreditNote.onlinePaymentNotice": "Cette facture a été payée en ligne. Pour rendre l'argent sur la carte du client, utilisez « Rembourser la carte par Stripe » dans la fenêtre de la facture. Un avoir rend l'argent autrement (comptant, autre mode) ou le garde au dossier.",
    "CreditNote.previewUnavailable": "Aperçu indisponible. Le montant sera calculé à l'enregistrement.",
    "CreditNote.refusedUnknown": "Le serveur refuse cet avoir. Rechargez la page, puis réessayez.",
    "Invoicing.save": "Enregistrer",
    "Invoicing.create": "Créer",
    "Invoicing.update": "Mettre à jour",
    "Invoicing.errorSelectCustomer": "Veuillez sélectionner un client",
    "Invoicing.errorAddItem": "Veuillez ajouter au moins un article",
    "Invoicing.removeDiscount": "Retirer le rabais",
    "Invoicing.priceRange.aboveMax": "{name} : le prix {got}$ dépasse le maximum autorisé ({max}$).",
    "Invoicing.priceRange.belowMin": "{name} : le prix {got}$ est inférieur au minimum autorisé ({min}$).",
    "Invoicing.priceRange.effectiveBelowMin": "{name} : ce rabais fait passer le prix à {got}$, sous le minimum ({min}$).",
    "Invoicing.quantityRange.aboveMax": "{name} : la quantité {got} dépasse le maximum autorisé ({max}).",
    "Invoicing.quantityRange.belowMin": "{name} : la quantité {got} est inférieure au minimum autorisé ({min}).",
    "Invoicing.quantityRange.notFixed": "{name} : la quantité est verrouillée à {fixed} pour ce service.",
    "Invoicing.quantityRange.mustBeInteger": "La quantité doit être un nombre entier pour ce service.",
    "Invoicing.zeroAmountConfirm.title": "Confirmer le paiement à 0 $",
    "Invoicing.zeroAmountConfirm.message": "Le service « {name} » a un prix de 0 $. Voulez-vous vraiment marquer cette facture comme payée ?",
    "Invoicing.zeroAmountConfirm.confirm": "Confirmer",
    "Invoicing.zeroAmountConfirm.cancel": "Annuler",
    "Invoicing.complimentaryConfirm.title": "Cette facture est-elle offerte ?",
    "Invoicing.complimentaryConfirm.withPayment": "Le total de cette facture est de 0 $ et vous avez inscrit un paiement de 0 $.",
    "Invoicing.complimentaryConfirm.withoutPayment": "Le total de cette facture est de 0 $ et aucun paiement n'est inscrit.",
    "Invoicing.complimentaryConfirm.consequence": "Si elle est offerte, elle sera considérée comme payée et comptée dans vos rapports.",
    "Invoicing.complimentaryConfirm.no": "Non, garder en brouillon",
    "Invoicing.complimentaryConfirm.yes": "Oui, elle est offerte",
    "Invoicing.errorMissingEmployeeOrCustomer": "Chaque service, produit ou autre article doit avoir un employé et un client assigné",
    "Invoicing.errorCancelledInvoice": "Impossible de modifier une facture annulée",
    "Invoicing.errorRefundedInvoice": "Impossible de modifier une facture remboursée",
    "Invoicing.errorLockedInvoice": "Cette facture est verrouillée et ne peut pas être modifiée (annulée ou remboursée)",
    "Invoicing.cancelledAutoNote": "Facture annulée : solde dû automatiquement mis à 0$",
    "Invoicing.errorInventoryReversalFailed": "L'inventaire n'a pas pu être restauré après l'annulation. Vérifiez les quantités en stock manuellement.",
    // ⛔ CE TEXTE A ÉTÉ CORRIGÉ le 2026-09-12 : il disait « La facture n'a pas
    //    été marquée comme payée », ce qui est FAUX depuis la décision du
    //    2026-09-05. L'encaissement est enregistré, seule la sortie de stock a
    //    échoué. La phrase mentait, et elle mentait sous un message de succès
    //    qui part deux lignes plus loin.
    "Invoicing.errorInventoryMovementFailed": "L'encaissement est enregistré, mais la sortie de stock a échoué. Vérifiez les quantités en stock, puis enregistrez de nouveau la facture pour relancer la déduction.",
    "Invoicing.errorInsufficientStock": "Stock insuffisant pour :",
    // Stock négatif permis (spec 2026-10-09, section 5).
    "Invoicing.negativeStockTitle": "Ce stock est à zéro",
    "Invoicing.negativeStockAt": "{product} n'a plus de stock à « {location} ».",
    "Invoicing.negativeStockNoLocation": "{product} n'a plus de stock.",
    "Invoicing.negativeStockQuestion": "Voulez-vous procéder à la vente ? Le stock sera enregistré comme négatif.",
    "Invoicing.negativeStockNo": "Non",
    "Invoicing.negativeStockYes": "Oui, vendre",
    "Invoicing.negativeStockSaveTitle": "Stock insuffisant",
    "Invoicing.negativeStockSaveIntro": "Ces produits passeront sous zéro :",
    "Invoicing.negativeStockSaveLine": "{product} : {sold} vendus, {available} en stock (stock après la vente : {after})",
    "Invoicing.negativeStockSaveLineOne": "{product} : 1 vendu, {available} en stock (stock après la vente : {after})",
    "Invoicing.negativeStockSaveLineAt": "{product} à « {location} » : {sold} vendus, {available} en stock (stock après la vente : {after})",
    "Invoicing.negativeStockSaveLineAtOne": "{product} à « {location} » : 1 vendu, {available} en stock (stock après la vente : {after})",
    "Invoicing.negativeStockSaveQuestion": "Voulez-vous enregistrer la facture quand même ?",
    "Invoicing.negativeStockSaveBack": "Revenir à la facture",
    "Invoicing.negativeStockSaveConfirm": "Enregistrer",
    "Invoicing.invoiceCreated": "Facture créée",
    "Invoicing.invoiceUpdated": "Facture mise à jour",
    "Invoicing.error": "Erreur",
    "Invoicing.partial": "Partiel",
    "Invoicing.refunded": "Remboursée",
    "Invoicing.unknown": "Inconnu",
    "Invoicing.customerRequired": "Client *",
    "Invoicing.statusLabel": "Statut",
    "Invoicing.tabDetails": "Détails",
    "Invoicing.tabItems": "Articles",
    "Invoicing.tabPayments": "Paiements",
    "Invoicing.descriptionPlaceholder": "Description",
    "Invoicing.method": "Méthode",
    "Invoicing.creditCard": "Carte de crédit",
    "Invoicing.online": "En ligne",
    "PaymentLinkButton.send": "Envoyer un lien de paiement",
    "PaymentLinkButton.copy": "Copier",
    "PaymentLinkButton.copied": "Copié",
    "PaymentLinkButton.errorTitle": "Impossible de créer le lien",
    "PaymentLinkButton.errorBody": "Réessayez dans un instant.",
    "PaymentLinkButton.revoke": "Annuler le lien",
    "PaymentLinkButton.revokeHint": "Le lien cesse de fonctionner immédiatement. Un paiement déjà effectué est conservé.",
    "PaymentLinkButton.revokeErrorTitle": "Impossible d'annuler le lien",
    "PaymentLinkButton.activeLink": "Un lien de paiement est actif",
    "PaymentLinkButton.sentByEmail": "Envoyé par courriel à",
    "PaymentLinkButton.sentBySms": "Envoyé par texto au",
    "PaymentLinkButton.showQr": "Aucune coordonnée : montrez ce code au client",
    "PaymentLinkButton.skipped.no_email": "Aucun courriel au dossier pour ce client.",
    "PaymentLinkButton.skipped.invalid_email": "Le courriel au dossier semble invalide.",
    "PaymentLinkButton.skipped.no_phone": "Aucun numéro de téléphone au dossier pour ce client.",
    "PaymentLinkButton.skipped.invalid_phone": "Le numéro au dossier semble invalide.",
    "PaymentLinkButton.skipped.missing_contact": "Le canal choisi n'a aucune coordonnée au dossier.",
    "PaymentLinkDialog.title": "Envoyer ce lien de paiement ?",
    "PaymentLinkDialog.loading": "Vérification des coordonnées du client…",
    "PaymentLinkDialog.channel.email": "Par courriel",
    "PaymentLinkDialog.channel.sms": "Par texto",
    "PaymentLinkDialog.channel.qr": "Montrer un code QR au comptoir",
    "PaymentLinkDialog.pickAChannel": "Choisissez d'abord comment l'envoyer.",
    "PaymentLinkDialog.confirm": "Envoyer",
    "PaymentLinkDialog.cancel": "Annuler",
    "PaymentLinkButton.revokeConfirmTitle": "Annuler ce lien de paiement ?",
    "PaymentLinkButton.revokeConfirmBody": "La cliente ne pourra plus payer avec le lien qu'elle a reçu. Un paiement déjà effectué est conservé.",
    "PaymentLinkButton.revokeConfirm": "Annuler le lien",
    "PaymentLinkButton.sendFailedBody": "Cet envoi n'a pas abouti. Vérifiez le statut de la facture et les coordonnées du client, puis réessayez.",
    "PaymentLinkButton.sendFailedTitle": "Cet envoi n'a pas abouti",
    "PaymentLinkButton.previewErrorTitle": "Impossible de préparer l'envoi",
    "PaymentLinkButton.previewErrorBody": "Réessayez dans un instant.",
    "PaymentLinkButton.nothingToCollectTitle": "Rien à encaisser",
    "PaymentLinkButton.nothingToCollectBody": "Cette facture n'a plus rien à encaisser. Vérifiez si le client a déjà payé au comptoir.",
    "PaymentLinkButton.createdNotDelivered": "Le lien a été créé, mais il n'a pas pu être envoyé. Copiez-le et remettez-le vous-même au client.",
    "PaymentLinkButton.forbiddenSendBody": "Vous n'avez pas la permission d'envoyer un lien de paiement pour cette facture. Demandez à un responsable.",
    "PaymentLinkButton.forbiddenRevokeBody": "Vous n'avez pas la permission d'annuler ce lien de paiement. Demandez à un responsable.",
    "PaymentLinkButton.accessCheckUnavailableBody": "Vos droits n'ont pas pu être vérifiés. Réessayez dans un instant.",
    "PaymentLinkButton.invoiceRefundedBody": "Cette facture a été remboursée. Aucun lien de paiement ne peut être envoyé.",
    "PaymentLinkSave.title": "Un lien de paiement est actif",
    "PaymentLinkSave.body": "Un client est peut-être en train de consulter le lien de paiement de cette facture. Pour la modifier, le lien doit d'abord être annulé. Le client qui cliquera dessus verra qu'il n'est plus valide.",
    "PaymentLinkSave.confirm": "Annuler le lien et enregistrer",
    "PaymentLinkSave.confirming": "Annulation du lien...",
    "PaymentLinkSave.keep": "Garder le lien",
    "PaymentLinkSave.askManager": "Un lien de paiement est actif pour cette facture. Demandez à un responsable de l'annuler avant de la modifier.",
    "PaymentLinkSave.close": "Fermer",
    "PaymentLinkSave.revokeFailedBody": "Rien n'a été enregistré. Réessayez dans un instant.",
    "PaymentLinkSave.nothingSaved": "Rien n'a été enregistré.",
    "PaymentLinkDialog.channelsLabel": "Canal d'envoi",
    "Invoicing.debitCard": "Débit",
    "Invoicing.visa": "Visa",
    "Invoicing.mastercard": "Mastercard",
    "Invoicing.amex": "AMEX",
    "Invoicing.eTransfer": "Virement électronique",
    "Invoicing.check": "Chèque",
    "Invoicing.notesLabel": "Notes",
    "Invoicing.saving": "Enregistrement...",
    "Invoicing.deleteTitle": "Supprimer la facture",
    "Invoicing.deleteConfirm":
      "Êtes-vous sûr de vouloir supprimer cette facture ? Cette action ne peut pas être annulée.",
    "Invoicing.invoiceDeleted": "Facture supprimée avec succès",
    "Invoicing.deleteSelected": "Supprimer la sélection",
    "Invoicing.deleteSelectedTitle": "Supprimer les factures sélectionnées",
    "Invoicing.deleteSelectedConfirm": "Êtes-vous sûr de vouloir supprimer les {count} facture(s) sélectionnée(s) ? Cette action ne peut pas être annulée.",
    "Invoicing.deleteSelectedDisabledHint": "Seuls les brouillons peuvent être supprimés",
    "Invoicing.invoicesDeleted": "{count} facture(s) supprimée(s) avec succès",
    "Invoicing.errorCannotDeleteNonDraft": "Seuls les brouillons peuvent être supprimés",
    "Invoicing.soldViaInvoice": "Vendu via la facture",
    "Invoicing.selectSection": "Sélectionner une section",
    "Invoicing.selectSubsection": "Sélectionner une sous-section",
    "Invoicing.noServicesInSubsection": "Aucun service dans cette sous-section",
    "Invoicing.filterByCategory": "Filtrer par catégorie",
    "Invoicing.filterBySupplier": "Filtrer par fournisseur",
    "Invoicing.activeFilters": "Filtres actifs",
    "Invoicing.clearAllFilters": "Tout effacer",
    "Invoicing.noProductsMatchingFilters": "Aucun produit correspondant aux filtres",
    "Invoicing.searchAndSelectService": "Rechercher et sélectionner un service...",
    "Invoicing.searchService": "Rechercher des services...",
    "Invoicing.noServiceFound": "Aucun service trouvé.",
    "Invoicing.searchAndSelectProduct": "Rechercher et sélectionner un produit...",
    "Invoicing.searchProduct": "Rechercher des produits...",
    "Invoicing.noProductFound": "Aucun produit trouvé.",
    "Invoicing.barcodeNotFoundTitle": "Aucun produit trouvé",
    "Invoicing.barcodeNotFoundDescriptionPrefix": "Aucun produit ne correspond au code-barres ",
    "Invoicing.barcodeOkButton": "OK",
    "Invoicing.filters": "Filtres",
    "Invoicing.active": "actif(s)",
    "Invoicing.category": "Catégorie",
    "Invoicing.supplier": "Fournisseur",
    "Invoicing.uncategorized": "Non catégorisé",
    "Invoicing.variablePrice": "Prix variable",
    "Invoicing.startingFrom": "À partir de",
    "Invoicing.otherServices": "Autres services",
    "Invoicing.typeService": "Service",
    "Invoicing.typeProduct": "Produit",
    "Invoicing.typeOther": "Autre",
    "Invoicing.show": "Afficher",
    "Invoicing.perPage": "par page",
    "Invoicing.pageInfo": "Page {current} sur {total}",
    "Invoicing.showingResults": "Affichage de {start} à {end} sur {total} factures",
    "Invoicing.giftCard": "Carte cadeau",
    "Invoicing.selectGiftCard": "Sélectionner une carte cadeau",
    "Invoicing.linkedGiftCards": "Cartes cadeaux liées",
    "Invoicing.enterGiftCardCode": "Entrer un code de carte cadeau",
    "Invoicing.giftCardCode": "Code de carte cadeau",
    "Invoicing.giftCardBalance": "Solde",
    "Invoicing.giftCardLookup": "Rechercher",
    "Invoicing.giftCardLookingUp": "Recherche en cours...",
    "Invoicing.giftCardNotFound": "Carte cadeau introuvable",
    "Invoicing.giftCardInactive": "Cette carte cadeau n'est pas active",
    "Invoicing.giftCardExpired": "Cette carte cadeau est expirée",
    "Invoicing.giftCardNoBalance": "Cette carte cadeau n'a plus de solde",
    "Invoicing.giftCardWrongSalon": "Cette carte cadeau appartient à un autre salon",
    "Invoicing.giftCardNoLinked": "Aucune carte cadeau liée trouvée pour ce client",
    "Invoicing.giftCardOrEnterCode": "Ou entrez un code de carte cadeau manuellement",
    "Invoicing.giftCardConfirm": "Utiliser cette carte",
    "Invoicing.addGiftCardRecharge": "Recharger une carte cadeau",
    "Invoicing.giftCardRecharge": "Recharge de carte cadeau",
    "Invoicing.typeGiftCardRecharge": "Recharge",
    "Invoicing.giftCardRechargeNoGCPayment": "Paiement par carte cadeau non disponible",
    "Invoicing.selectCardToRecharge": "Sélectionnez une carte cadeau à recharger",
    "Invoicing.noRechargeableCards": "Aucune carte cadeau rechargeable trouvée",
    "Invoicing.giftCardPurchase": "Achat de carte cadeau",
    "Invoicing.addGiftCardPurchase": "Nouvelle carte cadeau",
    "Invoicing.typeGiftCardPurchase": "Achat",
    "Invoicing.giftCardPurchaseAmount": "Montant",
    "Invoicing.giftCardExpirationDate": "Date d'expiration (optionnel)",
    "Invoicing.giftCardPurchaseNotes": "Notes (optionnel)",
    "Invoicing.addGiftCardPurchaseItem": "Ajouter la carte cadeau",
    "Invoicing.addGiftCardItemsPurchase": "Nouvelle carte cadeau articles",
    "Invoicing.giftCardItemsList": "Articles sur la carte",
    "Invoicing.giftCardItemsTotal": "Total",
    "Invoicing.giftCardMatchedItems": "Articles correspondants",
    "Invoicing.giftCardSelectItemToRedeem": "Sélectionner un article à utiliser",
    "Invoicing.giftCardItemRedeemed": "Utilisé",
    "Invoicing.giftCardItemAvailable": "Disponible",
    "Invoicing.giftCardMonetaryCards": "Cartes cadeaux monétaires",
    "Invoicing.giftCardServiceUnavailable": "Service non disponible",
    "Invoicing.giftCardProductUnavailable": "Produit non disponible",
    "Invoicing.perServiceEmployee": "Assigner un employé par service",
    "Invoicing.selectEmployee": "Sélectionner un employé",
    "Invoicing.employeeNotQualifiedTag": "(ne fait pas ce service)",
    "Invoicing.employeeNotQualifiedWarning": "Cet employé ne fait pas ce service : prix inchangé, à vérifier.",
    "Invoicing.perServiceCustomer": "Assigner un client par service",
    "Invoicing.selectItemCustomer": "Sélectionner un client",
    "Invoicing.searchClient": "Rechercher par nom, courriel, téléphone...",
    "Invoicing.noClientFound": "Aucun client trouvé.",
    "Invoicing.print": "Imprimer",
    "Invoicing.viewInvoice": "Voir la facture",
    "Invoicing.removeItem": "Retirer l'article",
    "Invoicing.removePayment": "Retirer le paiement",
    "Invoicing.toast.mergeNeedsInvoicingPermission": "Cette action demande la permission Facturation.",
    "Invoicing.printClientCopy": "Copie client",
    "Invoicing.printBusinessCopy": "Copie commerçant",
    "Invoicing.printBothCopies": "Les deux copies",
    "Invoicing.sendByEmail": "Envoyer par courriel",
    "Invoicing.sendReceiptByEmail": "Envoyer le reçu par courriel",
    "Invoicing.recipientEmail": "Courriel du destinataire",
    "Invoicing.selectRecipient": "Choisir un destinataire",
    "Invoicing.noEmailOnFile": "Aucun courriel au dossier",
    "Invoicing.noEmailOnFileForCustomer": "{name} n'a aucun courriel au dossier. Saisissez-en un manuellement ci-dessus.",
    "Invoicing.send": "Envoyer",
    "Invoicing.sending": "Envoi en cours...",
    "Invoicing.receiptSentSuccess": "Reçu envoyé avec succès!",
    "Invoicing.receiptSentError": "Erreur lors de l'envoi du reçu",

    // Cash Register
    "CashRegister.title": "Caisse",
    "CashRegister.open": "Ouverte",
    "CashRegister.closed": "Fermée",
    "CashRegister.todayInvoices": "Factures du jour",
    "CashRegister.reportTitle": "Rapport de caisse",
    "CashRegister.noClosingYet": "Aucune fermeture",
    "CashRegister.openRegister": "Ouvrir la caisse",
    "CashRegister.closeRegister": "Fermer la caisse",
    "CashRegister.openingAmount": "Montant d'ouverture",
    "CashRegister.prefilledFromClosing": "Pré-rempli depuis la fermeture du",
    "CashRegister.closingAmount": "Montant compté",
    "CashRegister.expectedAmount": "Montant attendu",
    "CashRegister.variance": "Écart",
    "CashRegister.cashBalance": "Balance de caisse",
    "CashRegister.currentBalance": "Solde actuel",
    "CashRegister.cashReceived": "Comptant reçu",
    "CashRegister.manualAdjustments": "Ajustements manuels",
    "CashRegister.addCash": "Ajouter du comptant",
    "CashRegister.withdrawCash": "Retirer du comptant",
    "CashRegister.amount": "Montant",
    "CashRegister.description": "Description",
    "CashRegister.movements": "Mouvements",
    "CashRegister.movementType": "Type",
    "CashRegister.movementTime": "Heure",
    "CashRegister.cashIn": "Entrée",
    "CashRegister.cashOut": "Sortie",
    "CashRegister.manualAdd": "Ajout manuel",
    "CashRegister.manualWithdraw": "Retrait manuel",
    "CashRegister.openingMovement": "Ouverture",
    "CashRegister.closingMovement": "Fermeture",
    "CashRegister.confirmClose": "Êtes-vous sûr de vouloir fermer la caisse?",
    "CashRegister.registerOpened": "Caisse ouverte avec succès",
    "CashRegister.registerClosed": "Caisse fermée",
    "CashRegister.alreadyOpen": "Une caisse est déjà ouverte pour ce salon. Elle est maintenant affichée.",
    "CashRegister.alreadyClosed": "Cette caisse était déjà fermée. La fermeture déjà enregistrée est conservée.",
    "CashRegister.movementAdded": "Mouvement ajouté avec succès",
    "CashRegister.cashBlockedTitle": "Caisse fermée",
    "CashRegister.cashBlockedDescription": "Les paiements comptant ne sont pas disponibles lorsque la caisse est fermée. Veuillez ouvrir la caisse d'abord.",
    "CashRegister.cashBlockedAskManager": "La caisse est fermée. Demandez à un responsable de l'ouvrir pour encaisser en espèces.",
    "CashRegister.notes": "Notes",
    "CashRegister.runningBalance": "Solde courant",
    "CashRegister.noMovements": "Aucun mouvement",
    "CashRegister.session": "Session",
    "CashRegister.openedBy": "Ouverte par",
    "CashRegister.closedBy": "Fermée par",
    "CashRegister.revenueByPayment": "Encaisse par types de paiements",
    "CashRegister.totalCollected": "Total encaissé",
    "CashRegister.salesSummary": "Sommaire des ventes",
    "CashRegister.services": "Services",
    "CashRegister.products": "Produits",
    "CashRegister.salesBeforeTax": "Ventes avant taxes",
    "CashRegister.greencircleFees": "Frais Green Circle",
    "CashRegister.salesWithTax": "Ventes avec taxes",
    "CashRegister.giftCardsIssued": "Cartes cadeaux émises",
    "CashRegister.totalSalesAndGC": "Total ventes et cartes cadeaux",
    "CashRegister.salesByEmployee": "Sommaire des ventes par vendeur (avant taxes)",
    "CashRegister.tipsByEmployee": "Sommaire des pourboires par vendeur",
    "CashRegister.cashTips": "Cash",
    "CashRegister.electronicTipsLabel": "Électronique",
    "CashRegister.tipWithdrawnNote": "Pourboire : facture {number}",
    "CashRegister.tipWithdrawSkippedRegisterClosed": "Caisse fermée : pourboire non prélevé.",
    "CashRegister.totalBeforeTax": "Total avant taxes",
    "CashRegister.grandTotal": "Grand total",
    "CashRegister.downloadReport": "Imprimer le rapport",
    "CashRegister.printReceipt": "Imprimer le reçu",
    "CashRegister.clientReceipt": "Reçu client",
    "CashRegister.businessReceipt": "Reçu commerce",
    "CashRegister.closingReport": "Rapport de fermeture",
    "CashRegister.selectSession": "Sélectionner une session",
    "CashRegister.activeSession": "Session active (provisoire)",
    "CashRegister.count": "Nb",
    "CashRegister.bills": "Billets",
    "CashRegister.coins": "Pièces",
    "CashRegister.denominationTotal": "Total",
    "CashRegister.openingDenominations": "Détail d'ouverture",
    "CashRegister.closingDenominations": "Détail de fermeture",
    "CashRegister.denominationBreakdown": "Ventilation des coupures",
    "CashRegister.cashRounding": "Arrondi",
    "CashRegister.amountReceived": "Montant reçu",
    "CashRegister.changeDue": "Monnaie à rendre",
    "CashRegister.invalidAmount": "Entrez un montant supérieur à zéro.",
    "CashRegister.withdrawExceedsBalance": "Le retrait dépasse le solde du tiroir",
    "CashRegister.depositDeclaration": "Déclaration dépôt",
    "CashRegister.depositDeclarationLabel": "Déclaration de dépôt (coffre)",
    "CashRegister.depositDeclarationHint": "Transférez une partie du comptant vers le coffre. L'écart reste calculé sur le tiroir complet; le dépôt est retiré du prochain fond de caisse.",
    "CashRegister.depositNotePlaceholder": "Note (optionnel)",
    "CashRegister.depositToVault": "Dépôt au coffre",
    "CashRegister.carriedFloat": "Fond reporté",
    "CashRegister.depositExceedsCount": "Le dépôt dépasse le montant compté.",
    "CashRegister.reconciliation": "Réconciliation du tiroir-caisse",
    "CashRegister.openingFloat": "Fond d'ouverture",
    "CashRegister.cashSales": "Ventes comptant",
    "CashRegister.cashRefunds": "Remboursements comptant",
    "CashRegister.manualAdditions": "Ajouts manuels",
    "CashRegister.manualWithdrawals": "Retraits manuels",
    "CashRegister.tipPayouts": "Pourboires remis du tiroir",
    "CashRegister.tipPayout": "Pourboire remis",
    "CashRegister.manualMovementsSection": "Mouvements manuels",
    "CashRegister.accrualNote": "Les ventes sont reconnues à la date d'émission (comptabilité d'engagement); l'encaisse reflète les paiements reçus pendant la session (trésorerie). Les deux peuvent légitimement différer.",
    "Invoicing.openDrawer": "Ouvrir le tiroir",
    "Invoicing.openDrawerFailed": "Le tiroir n'a pas pu être ouvert : l'impression n'est pas partie.",
    "Invoicing.drawerTicketTitle": "OUVERTURE MANUELLE DU TIROIR-CAISSE",
    "Invoicing.drawerTicketDate": "Date",
    "Invoicing.drawerTicketTime": "Heure",
    "Invoicing.drawerTicketBy": "Par",

    // CashRegister extras
    "CashRegister.taxTPS": "TPS (5%)",
    "CashRegister.taxTVQ": "TVQ (9.975%)",

    // Discount dialog
    "Invoicing.discountPercentageLabel": "Pourcentage",
    "Invoicing.discountFixedLabel": "Montant fixe",

    // Gift cards
    "Invoicing.giftCardPlaceholder": "XXXX-XXXX-XXXX",

    // Caisse — pourboire prélevé du tiroir
    "CashRegister.tipWithdrawFailed": "Le pourboire n'a pas été enregistré en caisse. L'argent est sorti du tiroir sans trace : ajoutez un mouvement manuel avant la fermeture.",
  },
};
